import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, API, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ColumnPicker from "@/components/ColumnPicker";
import { XeroSyncBar } from "@/components/finance/XeroSyncBar";
import { DirectDebitTab } from "@/components/finance/DirectDebitTab";
import { StageFilter, NO_STAGE } from "@/components/finance/StageFilter";
import { Landmark } from "lucide-react";
import {
  LayoutDashboard, Table as TableIcon, Grid3x3, Download, AlertTriangle,
  CheckCircle2, Eye, X, Search, Upload, CalendarClock, Receipt, Mail,
} from "lucide-react";

const YEARS = [2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026, 2027];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PAYMENT_METHODS = ["", "Direct Debit - Not Requested", "Direct Debit - Requested", "Direct Debit - In Place", "Bank Transfer"];
const BILLING_FREQUENCIES = ["", "Monthly", "Annual", "One-Off", "Other"];
const INVOICE_STATUSES = ["Not Started", "Draft", "Approved", "Sent"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ACTION_TYPES = [
  "Send payment reminder",
  "Escalate to account manager",
  "Schedule payment call",
  "Chase partner payment",
  "Flag to Finance Lead",
  "Other",
];
const STATUS_META = {
  on_track: { label: "On Track", cls: "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" },
  watch: { label: "Watch", cls: "border-[var(--warn)] text-[var(--warn)] bg-[var(--gold-soft)]" },
  in_arrears: { label: "In Arrears", cls: "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]" },
};

const TABLE_COLS = [
  { key: "relationship_start_date", label: "Relationship Start Date" },
  { key: "business_type", label: "Business Type" },
  { key: "account_manager", label: "Account Manager" },
  { key: "invoice_renewal_month", label: "Invoice Renewal Month" },
  { key: "invoice_status", label: "Invoice Status" },
  { key: "payment_method", label: "Payment Method" },
  { key: "billing_frequency", label: "Billing Frequency" },
  { key: "start_billing", label: "Start Billing" },
  { key: "instalments", label: "Instalments" },
  { key: "end_billing", label: "End Billing" },
  { key: "forecast_standard_annual", label: "Standard Fee (Fcst)" },
  { key: "forecast_additional_annual", label: "Additional Fee (Fcst)" },
  { key: "ytd_actual_total", label: "Actual (YTD)" },
  { key: "remaining_balance", label: "Remaining" },
  { key: "variance_pct", label: "Variance %" },
  { key: "status", label: "Status (Client)" },
  { key: "partner_forecast", label: "Partner Payment (Fcst)" },
  { key: "partner_actual", label: "Partner Payment (YTD)" },
  { key: "partner_status", label: "Status (Partner)" },
  { key: "year_end_date", label: "Year End Date" },
  { key: "xero_invoiced", label: "Xero Invoiced" },
  { key: "xero_paid", label: "Xero Paid" },
  { key: "xero_outstanding", label: "Xero Outstanding" },
];

const fmt = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function Finance() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.finance_enabled === true;
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useState("overview");
  const [rows, setRows] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [maxNext, setMaxNext] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [xeroTick, setXeroTick] = useState(0);

  async function load({ silent = false } = {}) {
    if (!silent) setInitialLoad(true);
    try {
      const [r, d] = await Promise.all([
        api.get(`/billing/${year}`),
        api.get(`/billing/dashboard/${year}`),
      ]);
      setRows(r.data.rows);
      setMaxNext(r.data.max_next_months || 0);
      setDashboard(d.data);
    } catch (e) {
      console.error(e);
    }
    if (!silent) setInitialLoad(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);
  const silentReload = () => load({ silent: true });

  return (
    <div className="p-8 md:p-10">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Finance & Billing · {year}</div>
          <h1 className="font-display text-4xl">Finance & Billing.</h1>
          <div className="text-sm text-[var(--ink-2)] mt-1">Xero-driven cash view and per-client billing profiles — all in one place.</div>
        </div>
        {/* Year selector was used by the old Client Billing / Monthly Tracker tabs.
            Those tabs are gone (REQ-1.11 / REQ-5.1) so the year is no longer meaningful here.
            Kept in state for the "Practice OS · {year}" chip in the header only. */}
        <XeroSyncBar isAdmin={isAdmin} canSync={isAdmin || isManager} onSynced={() => { setXeroTick((t) => t + 1); silentReload(); }} />
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--line)] flex gap-6 mb-6 overflow-x-auto">
        {[
          ["overview", "Overview", LayoutDashboard],
          ["xero", "Xero Invoices", Receipt],
          ["dd", "Direct Debit", Landmark],
        ].map(([key, label, Icon]) => {
          const on = tab === key;
          return (
            <button key={key} data-testid={`fin-tab-${key}`} onClick={() => setTab(key)}
              className={`py-2 flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${on ? "border-[var(--navy)] text-[var(--ink)]" : "border-transparent text-[var(--ink-2)] hover:text-[var(--ink)]"}`}>
              <Icon size={14} strokeWidth={1.5} /><span className="text-sm">{label}</span>
            </button>
          );
        })}
      </div>

      {initialLoad && <div className="text-center py-12 text-[var(--ink-2)]">Loading…</div>}
      {!initialLoad && tab === "overview" && <Overview key={xeroTick} />}
      {!initialLoad && tab === "dd" && <DirectDebitTab key={xeroTick} canAct={isAdmin || isManager} />}
      {!initialLoad && tab === "xero" && <XeroInvoices key={xeroTick} year={year} isAdmin={isAdmin} isManager={isManager} onReload={silentReload} />}
    </div>
  );
}

/* -------------------------------------------------- Overview (Xero-driven cash dashboard, v1.16) */
function Overview() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("monthly"); // weekly | monthly | yearly
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/xero/overview");
        setData(r.data);
      } catch (e) {
        setErr(formatErr(e));
      }
    })();
  }, []);

  if (err) return <div className="text-sm text-[var(--err)]" data-testid="fin-overview-err">{err}</div>;
  if (!data) return <div className="text-center py-12 text-[var(--ink-2)]">Loading Xero data…</div>;

  const s = data.summary;
  const cp = data.compare_periods;
  const cashSeries = data.cash_flow[period] || [];
  const pctCollected = s.invoiced > 0 ? Math.round((s.collected / s.invoiced) * 100) : 0;

  const dmy = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const empty = s.total_invoices === 0;
  if (empty) {
    return (
      <div className="card-flat p-10 text-center" data-testid="fin-overview-empty">
        <div className="overline mb-2">No Xero data yet</div>
        <div className="font-display text-2xl mb-2">Connect Xero and click Sync now to begin</div>
        <div className="text-sm text-[var(--ink-2)] max-w-md mx-auto">Use <b>Connect with Xero</b> / <b>Sync now</b> at the top right. Once synced, this Overview will show invoiced / collected / outstanding, a cash flow chart, and status breakdown.</div>
      </div>
    );
  }

  return (
    <div data-testid="fin-overview" className="space-y-4">
      {/* -------- 5 summary cards (Overdue added — v1.17) -------- */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <SumCard testid="fin-card-invoiced" label="Total invoiced" value={fmt(s.invoiced)} caption="Paid + awaiting only" />
        <SumCard testid="fin-card-collected" label="Collected" value={fmt(s.collected)} tone="ok" caption={`${pctCollected}% of invoiced`} />
        <SumCard testid="fin-card-outstanding" label="Outstanding" value={fmt(s.outstanding)} tone="warn" caption="Still owed" />
        <SumCard testid="fin-card-overdue" label="Overdue" value={fmt(s.overdue_value || 0)} tone="err" caption={`${s.overdue_count || 0} invoice${(s.overdue_count || 0) === 1 ? "" : "s"} past due date`} />
        <SumCard testid="fin-card-draft" label="Draft (pipeline)" value={fmt(s.draft_value)} tone="muted" caption="Not yet sent — excluded above" />
      </div>

      {/* -------- Cash flow + Status breakdown -------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-flat p-5" data-testid="fin-cashflow">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="font-medium text-[var(--ink-1)]">Money Coming In — Paid Invoices</div>
            </div>
            <div className="inline-flex items-center border border-[var(--line)] rounded overflow-hidden" data-testid="fin-cashflow-toggle">
              {["weekly", "monthly", "yearly"].map((p) => (
                <button
                  key={p}
                  data-testid={`fin-cashflow-${p}`}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-xs capitalize transition ${period === p ? "bg-[var(--ok)]/10 text-[var(--ok)] border-[var(--ok)]" : "text-[var(--ink-2)] hover:bg-[var(--bg)]"}`}
                >{p}</button>
              ))}
            </div>
          </div>
          <CashFlowBars data={cashSeries} period={period} />
          <div className="text-[10px] text-[var(--ink-2)] italic mt-3">Xero&apos;s export doesn&apos;t include an actual payment date, so this groups paid invoices by invoice date as the closest proxy.</div>
        </div>

        <div className="card-flat p-5" data-testid="fin-status-breakdown">
          <div className="font-medium text-[var(--ink-1)] mb-4">Status breakdown</div>
          <StatusDonut items={data.status_breakdown} />
        </div>
      </div>

      {/* -------- Compare Periods -------- */}
      <div className="card-flat p-5" data-testid="fin-compare-periods">
        {cp.has_prior ? (
          <>
            <div className="font-medium text-[var(--ink-1)] mb-3" data-testid="fin-compare-heading">
              Compare periods — since {dmy(cp.prior_upload_at)} (last upload)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="border border-[var(--line)] rounded p-4" data-testid="fin-compare-cash">
                <div className="overline mb-1.5">Cash collected</div>
                <div className="font-display text-2xl text-[var(--ok)]">+{fmt(cp.cash_collected_since)}</div>
                <div className="text-[10px] text-[var(--ink-2)] mt-1">New payments received this period</div>
              </div>
              <div className="border border-[var(--line)] rounded p-4" data-testid="fin-compare-new-inv">
                <div className="overline mb-1.5">New invoices raised</div>
                <div className="font-display text-2xl">
                  {cp.new_invoices_count} invoice{cp.new_invoices_count === 1 ? "" : "s"}
                  <span className="text-[var(--ink-2)] font-normal"> · </span>
                  {fmt(cp.new_invoices_value)}
                </div>
                <div className="text-[10px] text-[var(--ink-2)] mt-1">New business issued</div>
              </div>
            </div>
          </>
        ) : (
          <div data-testid="fin-compare-no-prior">
            <div className="font-medium text-[var(--ink-1)] mb-1">Compare periods</div>
            <div className="text-xs text-[var(--ink-2)]">First upload — no comparison yet. Upload another Xero CSV to see movement since the last one.</div>
          </div>
        )}
      </div>

      {/* -------- Forecast vs Actual (v1.20) -------- */}
      <ForecastVsActual />
    </div>
  );
}

function SumCard({ testid, label, value, caption, tone }) {
  const cls = tone === "ok" ? "text-[var(--ok)]" : tone === "warn" ? "text-[var(--warn)]" : tone === "muted" ? "text-[var(--ink-2)]" : "text-[var(--ink)]";
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="text-xs text-[var(--ink-2)] mb-1.5">{label}</div>
      <div className={`font-display text-2xl leading-none ${cls}`}>{value}</div>
      {caption && <div className="text-[10px] text-[var(--ink-2)] mt-1.5">{caption}</div>}
    </div>
  );
}

/**
 * Money-Coming-In cash-flow chart (v1.18) — proper SVG chart with:
 *  • Y-axis with 4 evenly spaced gridlines rounded to nice thousands
 *  • ALL data points visible in one view (no windowing)
 *  • Bar hover: highlighted bar (grey) + floating tooltip card showing period + £
 *  • Sparse X-axis labels so nothing overlaps regardless of series length
 */
function CashFlowBars({ data, period }) {
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null); // {i, x, y, label, value} on bar hover
  const [width, setWidth] = useState(720);

  // Measure the parent container width so the SVG scales to the card
  useEffect(() => {
    if (!containerRef.current) return;
    const update = () => setWidth(containerRef.current.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center text-[var(--ink-2)] text-xs gap-2" data-testid="fin-cashflow-empty">
        <div className="italic">No paid invoices in the uploaded data yet.</div>
        <div className="text-[10px]">Once an invoice moves to <b>PAID</b> in Xero and you re-upload, it will appear here.</div>
      </div>
    );
  }

  // ------- Chart geometry -------
  const H = 300;
  const padTop = 12, padBottom = 30, padLeft = 44, padRight = 12;
  const innerW = Math.max(200, width - padLeft - padRight);
  const innerH = H - padTop - padBottom;

  // Nice max: ceil to a rounded thousand so gridlines are clean
  const rawMax = Math.max(1, ...data.map((d) => d.value));
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const yMax = Math.ceil(rawMax / magnitude) * magnitude || 1;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * yMax));

  const barGap = data.length > 60 ? 1 : data.length > 30 ? 2 : 4;
  const bandW = innerW / data.length;
  const barW = Math.max(2, bandW - barGap);

  // Choose an x-label stride so ~10 labels fit
  const stride = Math.max(1, Math.ceil(data.length / 10));

  const short = (p) => {
    if (period === "monthly") {
      const [y, m] = p.split("-");
      return `${MONTHS[Math.max(0, (parseInt(m, 10) || 1) - 1)]} ${y.slice(2)}`;
    }
    if (period === "weekly") {
      // 2026-W03 → "W3 2026"
      const [y, w] = p.split("-W");
      return `W${parseInt(w, 10) || 0} ${y}`;
    }
    return p; // yearly
  };
  const shortAxis = (p) => {
    if (period === "monthly") {
      const [y, m] = p.split("-");
      return `${MONTHS[Math.max(0, (parseInt(m, 10) || 1) - 1)]} ${y.slice(2)}`;
    }
    if (period === "weekly") {
      // On the axis we only need a coarse anchor — show "MMM YY" derived from ISO week 1.
      // Approximation: Jan 4th is always in ISO week 1, so w=N → date = Jan 4 + (N-1)*7 days.
      const [y, w] = p.split("-W");
      const wn = Math.max(1, parseInt(w, 10) || 1);
      const d = new Date(Date.UTC(parseInt(y, 10), 0, 4 + (wn - 1) * 7));
      return `${MONTHS[d.getUTCMonth()]} ${String(y).slice(2)}`;
    }
    return short(p);
  };

  const yTickAbbrev = (v) => {
    if (v === 0) return "£0";
    if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
    if (v >= 1000) return `£${Math.round(v / 1000)}k`;
    return `£${v}`;
  };

  return (
    <div ref={containerRef} className="relative w-full" data-testid="fin-cashflow-chart">
      <svg
        width={width}
        height={H}
        className="block"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const mx = e.clientX - rect.left;
          // Ignore mouse over the axis area
          if (mx < padLeft || mx > padLeft + innerW) { setHover(null); return; }
          const i = Math.min(data.length - 1, Math.max(0, Math.floor((mx - padLeft) / bandW)));
          const d = data[i];
          if (!d) { setHover(null); return; }
          const h = (d.value / yMax) * innerH;
          setHover({ i, x: padLeft + i * bandW + bandW / 2, y: padTop + innerH - h, label: short(d.period), value: d.value });
        }}
      >
        {/* Y-axis gridlines + labels */}
        {ticks.map((t) => {
          const y = padTop + innerH - (t / yMax) * innerH;
          return (
            <g key={t}>
              <line x1={padLeft} y1={y} x2={padLeft + innerW} y2={y} stroke="var(--line)" strokeDasharray={t === 0 ? "" : "2 3"} />
              <text x={padLeft - 6} y={y + 4} textAnchor="end" fontSize="10" fill="var(--ink-2)" className="font-mono-data">{yTickAbbrev(t)}</text>
            </g>
          );
        })}

        {/* Highlight column behind the hovered bar (pointer-events: none — MUST not intercept mouse) */}
        {hover && (
          <rect
            x={padLeft + hover.i * bandW}
            y={padTop}
            width={bandW}
            height={innerH}
            fill="var(--ink-2)"
            opacity="0.10"
            style={{ pointerEvents: "none" }}
          />
        )}

        {/* Bars — pointer-events: none, all hover routed via the SVG-level onMouseMove above */}
        {data.map((d, i) => {
          const h = (d.value / yMax) * innerH;
          const x = padLeft + i * bandW + (bandW - barW) / 2;
          const y = padTop + innerH - h;
          const isHover = hover?.i === i;
          return (
            <rect
              key={d.period}
              x={x}
              y={y}
              width={barW}
              height={Math.max(0.5, h)}
              fill="var(--ok)"
              opacity={isHover ? 1 : 0.75}
              rx={1}
              style={{ pointerEvents: "none" }}
            />
          );
        })}

        {/* X-axis labels — every stride'th tick */}
        {data.map((d, i) => {
          if (i % stride !== 0 && i !== data.length - 1) return null;
          return (
            <text
              key={`xl-${d.period}`}
              x={padLeft + i * bandW + bandW / 2}
              y={H - 10}
              textAnchor="middle"
              fontSize="9"
              fill="var(--ink-2)"
              className="font-mono-data"
              style={{ pointerEvents: "none" }}
            >{shortAxis(d.period)}</text>
          );
        })}
      </svg>

      {/* Floating tooltip card — big, clear £ amount */}
      {hover && (
        <div
          className="absolute pointer-events-none bg-[var(--surface)] border-2 border-[var(--ok)] shadow-xl rounded px-3 py-2 whitespace-nowrap z-10"
          style={{
            left: Math.min(Math.max(hover.x - 80, 0), width - 180),
            top: Math.max(4, hover.y - 72),
          }}
          data-testid="fin-cashflow-tooltip"
        >
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">{hover.label}</div>
          <div className="font-display text-lg text-[var(--ok)] font-mono-data leading-tight" data-testid="fin-cashflow-tooltip-value">{fmt(hover.value)}</div>
        </div>
      )}
    </div>
  );
}

/**
 * Forecast vs Actual (v1.20). Two lenses over the same Xero dataset:
 *  • Cash-in — money expected in (Awaiting due-date) vs money that landed (Paid amount_paid)
 *  • Revenue — revenue booked (invoice total) vs revenue collected (amount_paid)
 * Grouped bars, one grey (Forecast) + one green (Actual), period toggle W/M/Q/Y + custom date range.
 */
function ForecastVsActual() {
  const [mode, setMode] = useState("cash");           // cash | revenue
  const [period, setPeriod] = useState("monthly");    // weekly | monthly | quarterly | yearly
  const [rangePreset, setRangePreset] = useState("last-12m");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [hover, setHover] = useState(null);
  const containerRef = useRef(null);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    if (!containerRef.current) return;
    const update = () => setWidth(containerRef.current.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const effRange = useMemo(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (rangePreset === "custom") return { from: customFrom, to: customTo };
    if (rangePreset === "all") return { from: "", to: "" };
    if (rangePreset === "this-year") return { from: `${today.getFullYear()}-01-01`, to: iso(today) };
    if (rangePreset === "last-12m") { const d = new Date(today); d.setMonth(d.getMonth() - 12); return { from: iso(d), to: iso(today) }; }
    if (rangePreset === "next-12m") { const d = new Date(today); d.setMonth(d.getMonth() + 12); return { from: iso(today), to: iso(d) }; }
    if (rangePreset === "last-3m") { const d = new Date(today); d.setMonth(d.getMonth() - 3); return { from: iso(d), to: iso(today) }; }
    return { from: "", to: "" };
  }, [rangePreset, customFrom, customTo]);

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams({ mode, period });
        if (effRange.from) params.set("from", effRange.from);
        if (effRange.to) params.set("to", effRange.to);
        const r = await api.get(`/xero/forecast?${params.toString()}`);
        setData(r.data); setErr("");
      } catch (e) { setErr(formatErr(e)); }
    })();
  }, [mode, period, effRange.from, effRange.to]);

  return (
    <div className="card-flat p-5" data-testid="fin-forecast">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-medium text-[var(--ink-1)]">Forecast vs Actual</div>
          <div className="text-[10px] text-[var(--ink-2)] mt-0.5">
            {mode === "cash"
              ? "Money expected in (Awaiting invoices, bucketed by due date) vs money that landed (Paid amount)."
              : "Revenue booked (invoice total, bucketed by invoice date) vs revenue collected."}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          {/* Mode toggle */}
          <div className="inline-flex items-center border border-[var(--line)] rounded overflow-hidden" data-testid="fin-forecast-mode">
            {[["cash", "Cash-in"], ["revenue", "Revenue"]].map(([k, l]) => (
              <button key={k} data-testid={`fin-forecast-mode-${k}`} onClick={() => setMode(k)} className={`px-2.5 py-1 text-[10px] uppercase tracking-wider transition ${mode === k ? "bg-[var(--navy)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--bg)]"}`}>{l}</button>
            ))}
          </div>
          {/* Period toggle */}
          <div className="inline-flex items-center border border-[var(--line)] rounded overflow-hidden" data-testid="fin-forecast-period">
            {[["weekly", "W"], ["monthly", "M"], ["quarterly", "Q"], ["yearly", "Y"]].map(([k, l]) => (
              <button key={k} data-testid={`fin-forecast-period-${k}`} onClick={() => setPeriod(k)} className={`px-2 py-1 text-[10px] font-mono-data transition ${period === k ? "bg-[var(--ok)]/15 text-[var(--ok)]" : "text-[var(--ink-2)] hover:bg-[var(--bg)]"}`}>{l}</button>
            ))}
          </div>
          {/* Range preset */}
          <select data-testid="fin-forecast-range" className="input-slim !py-1 !text-[10px]" value={rangePreset} onChange={(e) => setRangePreset(e.target.value)}>
            <option value="last-3m">Last 3 months</option>
            <option value="last-12m">Last 12 months</option>
            <option value="next-12m">Next 12 months</option>
            <option value="this-year">This year</option>
            <option value="all">All time</option>
            <option value="custom">Custom…</option>
          </select>
          {rangePreset === "custom" && (
            <>
              <input data-testid="fin-forecast-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="input-slim !py-1 !text-[10px]" />
              <input data-testid="fin-forecast-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="input-slim !py-1 !text-[10px]" />
            </>
          )}
        </div>
      </div>

      {err && <div className="text-xs text-[var(--err)]" data-testid="fin-forecast-err">{err}</div>}
      {!data && !err && <div className="text-center py-6 text-[var(--ink-2)] text-xs">Loading…</div>}
      {data && (
        <>
          {/* Summary strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <MiniStat label="Forecast" value={fmt(data.total_forecast)} tone="muted" />
            <MiniStat label="Actual" value={fmt(data.total_actual)} tone="ok" />
            <MiniStat label="Variance" value={`${data.total_variance >= 0 ? "+" : ""}${fmt(data.total_variance)}`} tone={data.total_variance >= 0 ? "ok" : "err"} />
            <MiniStat label="Collection" value={data.collection_pct != null ? `${data.collection_pct}%` : "—"} tone={data.collection_pct != null && data.collection_pct < 80 ? "warn" : "muted"} />
          </div>
          {/* Grouped bars */}
          <div ref={containerRef} className="relative w-full" data-testid="fin-forecast-chart">
            {(data.series || []).length === 0 ? (
              <div className="h-48 flex items-center justify-center text-[var(--ink-2)] text-xs italic">No data in the selected range.</div>
            ) : (
              <ForecastBars width={width} series={data.series} period={period} onHover={setHover} hover={hover} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ForecastBars({ width, series, period, onHover, hover }) {
  const H = 260, padTop = 12, padBottom = 30, padLeft = 44, padRight = 12;
  const innerW = Math.max(200, width - padLeft - padRight);
  const innerH = H - padTop - padBottom;

  const rawMax = Math.max(1, ...series.flatMap((d) => [d.forecast, d.actual]));
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const yMax = Math.ceil(rawMax / magnitude) * magnitude || 1;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * yMax));

  const bandW = innerW / series.length;
  const barGap = 2;
  const barW = Math.max(2, (bandW - barGap * 3) / 2);
  const stride = Math.max(1, Math.ceil(series.length / 10));

  const shortAxis = (p) => {
    if (period === "monthly") { const [y, m] = p.split("-"); return `${MONTHS[Math.max(0, (parseInt(m, 10) || 1) - 1)]} ${y.slice(2)}`; }
    if (period === "quarterly") return p.replace(/^\d{2}(\d{2})/, "$1"); // 2026-Q1 → 26-Q1
    if (period === "weekly") { const [y, w] = p.split("-W"); const wn = Math.max(1, parseInt(w, 10) || 1); const d = new Date(Date.UTC(parseInt(y, 10), 0, 4 + (wn - 1) * 7)); return `${MONTHS[d.getUTCMonth()]} ${String(y).slice(2)}`; }
    return p;
  };
  const yTickAbbrev = (v) => { if (v === 0) return "£0"; if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`; if (v >= 1000) return `£${Math.round(v / 1000)}k`; return `£${v}`; };

  return (
    <>
      <svg
        width={width}
        height={H}
        className="block"
        onMouseLeave={() => onHover(null)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const mx = e.clientX - rect.left;
          if (mx < padLeft || mx > padLeft + innerW) { onHover(null); return; }
          const i = Math.min(series.length - 1, Math.max(0, Math.floor((mx - padLeft) / bandW)));
          const d = series[i];
          if (!d) { onHover(null); return; }
          const topY = padTop + innerH - (Math.max(d.forecast, d.actual) / yMax) * innerH;
          onHover({ i, x: padLeft + i * bandW + bandW / 2, y: topY, period: d.period, label: shortAxis(d.period), ...d });
        }}
      >
        {ticks.map((t) => {
          const y = padTop + innerH - (t / yMax) * innerH;
          return (
            <g key={t}>
              <line x1={padLeft} y1={y} x2={padLeft + innerW} y2={y} stroke="var(--line)" strokeDasharray={t === 0 ? "" : "2 3"} />
              <text x={padLeft - 6} y={y + 4} textAnchor="end" fontSize="10" fill="var(--ink-2)" className="font-mono-data">{yTickAbbrev(t)}</text>
            </g>
          );
        })}
        {hover && (
          <rect x={padLeft + hover.i * bandW} y={padTop} width={bandW} height={innerH} fill="var(--ink-2)" opacity="0.08" style={{ pointerEvents: "none" }} />
        )}
        {series.map((d, i) => {
          const hFcst = (d.forecast / yMax) * innerH;
          const hAct = (d.actual / yMax) * innerH;
          const groupX = padLeft + i * bandW + barGap;
          return (
            <g key={d.period} style={{ pointerEvents: "none" }}>
              <rect x={groupX} y={padTop + innerH - hFcst} width={barW} height={Math.max(0.5, hFcst)} fill="var(--ink-2)" opacity="0.35" rx={1} />
              <rect x={groupX + barW + barGap} y={padTop + innerH - hAct} width={barW} height={Math.max(0.5, hAct)} fill="var(--ok)" opacity={0.85} rx={1} />
            </g>
          );
        })}
        {series.map((d, i) => {
          if (i % stride !== 0 && i !== series.length - 1) return null;
          return (
            <text key={`xl-${d.period}`} x={padLeft + i * bandW + bandW / 2} y={H - 10} textAnchor="middle" fontSize="9" fill="var(--ink-2)" className="font-mono-data" style={{ pointerEvents: "none" }}>{shortAxis(d.period)}</text>
          );
        })}
      </svg>
      {/* Legend */}
      <div className="flex gap-4 mt-1 text-[10px] text-[var(--ink-2)]" data-testid="fin-forecast-legend">
        <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm bg-[var(--ink-2)] opacity-35" /> Forecast</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2.5 rounded-sm bg-[var(--ok)] opacity-85" /> Actual</span>
      </div>
      {hover && (
        <div
          className="absolute pointer-events-none bg-[var(--surface)] border-2 border-[var(--ok)] shadow-xl rounded px-3 py-2 whitespace-nowrap z-10"
          style={{ left: Math.min(Math.max(hover.x - 90, 0), width - 200), top: Math.max(4, hover.y - 90) }}
          data-testid="fin-forecast-tooltip"
        >
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)]">{hover.label}</div>
          <div className="text-[10px] text-[var(--ink-2)] mt-1">Forecast: <span className="font-mono-data text-[var(--ink-1)]">{fmt(hover.forecast)}</span></div>
          <div className="text-[10px] text-[var(--ok)]">Actual: <span className="font-mono-data">{fmt(hover.actual)}</span></div>
          <div className={`text-[10px] mt-1 ${hover.variance >= 0 ? "text-[var(--ok)]" : "text-[var(--err)]"}`}>Variance: <span className="font-mono-data">{hover.variance >= 0 ? "+" : ""}{fmt(hover.variance)}</span></div>
        </div>
      )}
    </>
  );
}


function StatusDonut({ items }) {
  const total = items.reduce((a, i) => a + i.value, 0);
  const size = 140, thickness = 22, r = (size - thickness) / 2, circ = 2 * Math.PI * r;
  const palette = {
    "Paid": "var(--ok)",
    "Awaiting Payment": "var(--warn)",
    "Overdue": "var(--err)",
    "Draft": "var(--ink-2)",
  };
  let offset = 0;
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <svg width={size} height={size} data-testid="fin-status-donut-svg">
        <circle r={r} cx={size / 2} cy={size / 2} fill="transparent" stroke="var(--line)" strokeWidth={thickness} />
        {items.map((it) => {
          if (!total || it.value <= 0) return null;
          const dash = (it.value / total) * circ;
          const arc = (
            <circle key={it.status} r={r} cx={size / 2} cy={size / 2} fill="transparent"
              stroke={palette[it.status]} strokeWidth={thickness}
              strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`} />
          );
          offset += dash;
          return arc;
        })}
      </svg>
      <ul className="text-xs space-y-2 flex-1 min-w-[160px]" data-testid="fin-status-legend">
        {items.map((it) => (
          <li key={it.status} className="flex items-center gap-2" data-testid={`fin-status-${it.status.toLowerCase().replace(/\s+/g,'-')}`}>
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: palette[it.status] }} />
            <span className="flex-1 truncate"><b>{it.status}</b> <span className="text-[var(--ink-2)]">({it.count})</span></span>
            <span className="font-mono-data whitespace-nowrap">{fmt(it.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  const cls = tone === "err" ? "text-[var(--err)]" : tone === "ok" ? "text-[var(--ok)]" : tone === "gold" ? "text-[var(--warn)]" : tone === "navy" ? "text-[var(--navy)]" : tone === "muted" ? "text-[var(--ink-2)]" : "text-[var(--ink)]";
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="overline mb-2">{label}</div>
      <div className={`font-display text-2xl leading-none ${cls}`}>{value}</div>
    </div>
  );
}

function MiniDonut({ title, data, colors }) {
  const entries = Object.entries(data);
  const total = entries.reduce((a, [, v]) => a + v, 0);
  const size = 130, thickness = 20, r = (size - thickness) / 2, circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="card-flat p-5">
      <div className="overline mb-3">{title}</div>
      <div className="flex items-center gap-4">
        <svg width={size} height={size}>
          <circle r={r} cx={size / 2} cy={size / 2} fill="transparent" stroke="var(--line)" strokeWidth={thickness} />
          {entries.map(([k, v]) => {
            if (!total || v <= 0) return null;
            const dash = (v / total) * circ;
            const arc = (
              <circle key={k} r={r} cx={size / 2} cy={size / 2} fill="transparent" stroke={colors[k] || "var(--ink-2)"} strokeWidth={thickness} strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-offset} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
            );
            offset += dash;
            return arc;
          })}
          <text x="50%" y="52%" textAnchor="middle" style={{ fontSize: 14, fill: "var(--ink)" }}>{fmt(total)}</text>
        </svg>
        <ul className="text-xs space-y-1 flex-1">
          {entries.map(([k, v]) => (
            <li key={k} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm" style={{ background: colors[k] || "var(--ink-2)" }} />
              <span className="flex-1">{k}</span>
              <span className="font-mono-data">{fmt(v)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Clients & Partners Table */
function ClientsTable({ rows, year, canEdit, isAdmin, isManager, onReload }) {
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ business_type: "", account_manager: "", payment_method: "", status: "" });
  const [visibleCols, setVisibleCols] = useState(new Set(TABLE_COLS.map(c => c.key)));
  const [actionFor, setActionFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [applyingYE, setApplyingYE] = useState(false);
  const fileRef = useRef(null);

  const filtered = useMemo(() => rows.filter((r) => {
    if (q && !(r.client_name || "").toLowerCase().includes(q.toLowerCase())) return false;
    if (filters.business_type && r.business_type !== filters.business_type) return false;
    if (filters.account_manager && r.account_manager !== filters.account_manager) return false;
    if (filters.payment_method && (r.payment_method || "") !== filters.payment_method) return false;
    if (filters.status && r.status !== filters.status) return false;
    return true;
  }), [rows, q, filters]);

  const distinct = (key) => Array.from(new Set(rows.map((r) => r[key]).filter(Boolean))).sort();

  async function exportCsv() {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
    const orderedKeys = ["client_name", ...TABLE_COLS.filter(c => visibleCols.has(c.key)).map(c => c.key)];
    params.set("columns", orderedKeys.join(","));
    const r = await fetch(`${API}/billing/${year}/export?${params.toString()}`, { credentials: "include" });
    const blob = await r.blob();
    const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = `billing_${year}.csv`; link.click();
  }

  const patchYear = (cid, body) => api.patch(`/billing/${year}/client/${cid}`, body).then(onReload);
  const patchClient = async (cid, body) => {
    // PUT client: need full body — fetch, merge, put
    const cur = (await api.get(`/clients/${cid}`)).data;
    await api.put(`/clients/${cid}`, { ...cur, ...body });
    onReload();
  };

  async function handleImport(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true); setImportMsg("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${API}/billing/${year}/bulk-import`, { method: "POST", body: fd, credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Import failed");
      setImportMsg(`Imported ${j.ok}/${j.total} rows`);
      onReload();
    } catch (err) {
      setImportMsg(`Failed: ${err.message}`);
    }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function applyYearEndAll() {
    if (!window.confirm(`Set Year End Date for ALL clients for ${year}?\n\n- Companies (Pvt Ltd/LLP): uses their Companies House accounting reference (month/day) applied to ${year}\n- Self-Assessment / Other: ${year}-04-05\n\nThis will overwrite any existing values on the ${year} billing view.`)) return;
    setApplyingYE(true);
    try {
      const r = await api.post(`/billing/set-year-end-all/${year}`);
      alert(`Year End Date set on ${r.data.updated} client${r.data.updated === 1 ? "" : "s"} for ${year}.`);
      onReload();
    } catch (e) {
      alert(formatErr(e.response?.data?.detail) || e.message);
    }
    setApplyingYE(false);
  }

  return (
    <>
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative">
          <Search size={13} strokeWidth={1.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <input data-testid="fin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="input-slim pl-8 w-56" />
        </div>
        <select data-testid="fin-filter-btype" className="input-slim" value={filters.business_type} onChange={(e) => setFilters({ ...filters, business_type: e.target.value })}>
          <option value="">Business Type · All</option>
          {distinct("business_type").map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="fin-filter-am" className="input-slim" value={filters.account_manager} onChange={(e) => setFilters({ ...filters, account_manager: e.target.value })}>
          <option value="">Account Manager · All</option>
          {distinct("account_manager").map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="fin-filter-pm" className="input-slim" value={filters.payment_method} onChange={(e) => setFilters({ ...filters, payment_method: e.target.value })}>
          <option value="">Payment Method · All</option>
          <option value="Direct Debit">Direct Debit</option>
          <option value="Bank Transfer">Bank Transfer</option>
        </select>
        <select data-testid="fin-filter-status" className="input-slim" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">Status · All</option>
          <option value="on_track">On Track</option>
          <option value="watch">Watch</option>
          <option value="in_arrears">In Arrears</option>
        </select>
        <div className="flex-1" />
        {(isAdmin || isManager) && (
          <button data-testid="fin-year-end-all" disabled={applyingYE} onClick={applyYearEndAll} className="btn-ghost flex items-center gap-2 text-xs" title="Populate Year End on every client">
            <CalendarClock size={12} strokeWidth={1.5} /> {applyingYE ? "Applying…" : "Year End all clients"}
          </button>
        )}
        {isAdmin && (
          <>
            <button data-testid="fin-bulk-import" disabled={importing} onClick={() => fileRef.current?.click()} className="btn-ghost flex items-center gap-2 text-xs" title="Bulk import billing (CSV / XLSX)">
              <Upload size={12} strokeWidth={1.5} /> {importing ? "Importing…" : "Bulk Import"}
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={handleImport} className="hidden" data-testid="fin-import-file" />
          </>
        )}
        <ColumnPicker columns={TABLE_COLS} storageKey="finchley.billing.cols.v3" onChange={setVisibleCols} testid="fin-cols" />
        <button data-testid="fin-export-csv" onClick={exportCsv} className="btn-ghost flex items-center gap-2 text-xs"><Download size={12} strokeWidth={1.5} /> Export CSV</button>
      </div>
      {importMsg && <div className="text-xs mb-3 text-[var(--ink-2)]" data-testid="fin-import-msg">{importMsg}</div>}

      <div className="card-flat overflow-x-auto">
        <table className="tbl min-w-[1600px]">
          <thead><tr>
            <th className="sticky left-0 bg-[var(--surface)] z-10">Client</th>
            {TABLE_COLS.filter(c => visibleCols.has(c.key)).map(c => <th key={c.key}>{c.label}</th>)}
            <th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.client_id} data-testid={`fin-row-${r.client_id}`}>
                <td className="sticky left-0 bg-[var(--surface)] z-10 font-medium">{r.client_name}</td>
                {visibleCols.has("relationship_start_date") && (
                  canEdit ? (
                    <EditableDMYDate value={r.relationship_start_date} onSave={(v) => patchYear(r.client_id, { relationship_start_date: v })} testid={`fin-rsd-${r.client_id}`} />
                  ) : (
                    <td className="font-mono-data text-xs">{r.relationship_start_date || "—"}</td>
                  )
                )}
                {visibleCols.has("business_type") && <td>{r.business_type || "—"}</td>}
                {visibleCols.has("account_manager") && <td className="text-xs text-[var(--ink-2)]">{r.account_manager || "—"}</td>}
                {visibleCols.has("invoice_renewal_month") && (
                  <td className="text-xs">
                    {canEdit ? (
                      <select data-testid={`fin-irm-${r.client_id}`} className="input-slim text-[10px] !py-0.5 !px-1"
                        value={r.invoice_renewal_month || ""}
                        onChange={(e) => patchYear(r.client_id, { invoice_renewal_month: e.target.value })}>
                        <option value="">—</option>
                        {MONTH_NAMES.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    ) : (r.invoice_renewal_month || "—")}
                  </td>
                )}
                {visibleCols.has("invoice_status") && (
                  <td className="text-xs">
                    {canEdit ? (
                      <select data-testid={`fin-is-${r.client_id}`} className="input-slim text-[10px] !py-0.5 !px-1"
                        value={r.invoice_status || "Not Started"}
                        onChange={(e) => patchYear(r.client_id, { invoice_status: e.target.value })}>
                        {INVOICE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (r.invoice_status || "Not Started")}
                  </td>
                )}
                {visibleCols.has("payment_method") && (
                  <td className="text-xs">
                    {canEdit ? (
                      <select data-testid={`fin-pm-${r.client_id}`} className="input-slim text-[10px] !py-0.5 !px-1"
                        value={r.payment_method || ""}
                        onChange={(e) => patchClient(r.client_id, { payment_method: e.target.value })}>
                        {PAYMENT_METHODS.map(pm => <option key={pm} value={pm}>{pm || "—"}</option>)}
                      </select>
                    ) : (r.payment_method || "—")}
                  </td>
                )}
                {visibleCols.has("billing_frequency") && (
                  <td className="text-xs">
                    {canEdit ? (
                      <select data-testid={`fin-bf-${r.client_id}`} className="input-slim text-[10px] !py-0.5 !px-1"
                        value={r.billing_frequency || ""}
                        onChange={(e) => patchYear(r.client_id, { billing_frequency: e.target.value })}>
                        {BILLING_FREQUENCIES.map(bf => <option key={bf} value={bf}>{bf || "—"}</option>)}
                      </select>
                    ) : (r.billing_frequency || "—")}
                  </td>
                )}
                {visibleCols.has("start_billing") && <EditableDate canEdit={canEdit} value={r.start_billing} onSave={(v) => patchYear(r.client_id, { start_billing: v })} testid={`fin-sb-${r.client_id}`} />}
                {visibleCols.has("instalments") && <EditableNumber canEdit={canEdit} value={r.instalments} onSave={(v) => patchYear(r.client_id, { instalments: parseInt(v || 0) })} testid={`fin-inst-${r.client_id}`} />}
                {visibleCols.has("end_billing") && <td className="font-mono-data text-xs text-[var(--ink-2)]">{r.end_billing ? (r.end_billing || "").slice(0, 7) : "—"}</td>}
                {visibleCols.has("forecast_standard_annual") && <EditableMoney canEdit={canEdit} value={r.forecast_standard_annual} onSave={(v) => patchYear(r.client_id, { forecast_standard_annual: v })} />}
                {visibleCols.has("forecast_additional_annual") && <EditableMoney canEdit={canEdit} value={r.forecast_additional_annual} onSave={(v) => patchYear(r.client_id, { forecast_additional_annual: v })} />}
                {visibleCols.has("ytd_actual_total") && <td className="font-mono-data text-xs">{fmt(r.ytd_actual_total)}</td>}
                {visibleCols.has("remaining_balance") && <td className="font-mono-data text-xs">{fmt(r.remaining_balance)}</td>}
                {visibleCols.has("variance_pct") && <td className={`font-mono-data text-xs ${r.variance_pct < 0 ? "text-[var(--err)]" : "text-[var(--ok)]"}`}>{r.variance_pct}%</td>}
                {visibleCols.has("status") && (
                  <td>
                    {canEdit ? (
                      <select
                        data-testid={`fin-status-${r.client_id}`}
                        className={`input-slim text-[10px] !py-0.5 !px-1 ${STATUS_META[r.status]?.cls || ""}`}
                        value={r.status_override || ""}
                        onChange={(e) => patchYear(r.client_id, { status_override: e.target.value || null })}
                        title={r.status_override ? "Manually overridden" : `Computed: ${STATUS_META[r.status_computed]?.label}`}
                      >
                        <option value="">Auto ({STATUS_META[r.status_computed]?.label})</option>
                        <option value="on_track">On Track</option>
                        <option value="watch">Watch</option>
                        <option value="in_arrears">In Arrears</option>
                      </select>
                    ) : (
                      <span className={`status-pill text-[10px] ${STATUS_META[r.status]?.cls}`}>{STATUS_META[r.status]?.label}</span>
                    )}
                  </td>
                )}
                {visibleCols.has("partner_forecast") && <EditableMoney canEdit={canEdit} value={r.partner_forecast} onSave={(v) => patchYear(r.client_id, { partner_forecast_annual: v })} />}
                {visibleCols.has("partner_actual") && <EditableMoney canEdit={canEdit} value={r.partner_actual} onSave={(v) => patchYear(r.client_id, { partner_actual_annual: v })} />}
                {visibleCols.has("partner_status") && (
                  <td>
                    {canEdit ? (
                      <select
                        data-testid={`fin-partner-status-${r.client_id}`}
                        className={`input-slim text-[10px] !py-0.5 !px-1 ${r.partner_status ? STATUS_META[r.partner_status]?.cls : ""}`}
                        value={r.partner_status_override || ""}
                        onChange={(e) => patchYear(r.client_id, { partner_status_override: e.target.value || null })}
                        title={r.partner_status_override ? "Manually overridden" : `Computed: ${r.partner_status_computed ? STATUS_META[r.partner_status_computed]?.label : "—"}`}
                      >
                        <option value="">Auto ({r.partner_status_computed ? STATUS_META[r.partner_status_computed]?.label : "—"})</option>
                        <option value="on_track">On Track</option>
                        <option value="watch">Watch</option>
                        <option value="in_arrears">In Arrears</option>
                      </select>
                    ) : (
                      r.partner_status ? <span className={`status-pill text-[10px] ${STATUS_META[r.partner_status]?.cls}`}>{STATUS_META[r.partner_status]?.label}</span> : <span className="text-[var(--ink-2)]">—</span>
                    )}
                  </td>
                )}
                {visibleCols.has("year_end_date") && <EditableDate canEdit={canEdit} value={r.year_end_date} onSave={(v) => patchYear(r.client_id, { year_end_date: v })} testid={`fin-ye-${r.client_id}`} />}
                {visibleCols.has("xero_invoiced") && <td className="font-mono-data text-xs">{r.xero_invoiced ? fmt(r.xero_invoiced) : "—"}</td>}
                {visibleCols.has("xero_paid") && <td className="font-mono-data text-xs text-[var(--ok)]">{r.xero_paid ? fmt(r.xero_paid) : "—"}</td>}
                {visibleCols.has("xero_outstanding") && <td className={`font-mono-data text-xs ${r.xero_outstanding > 0 ? "text-[var(--err)]" : "text-[var(--ink-2)]"}`}>{r.xero_outstanding ? fmt(r.xero_outstanding) : "—"}</td>}
                <td className="text-right">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`fin-detail-${r.client_id}`} onClick={() => setDetailFor(r)} className="btn-ghost !py-1 !px-2 text-xs" title="View detail"><Eye size={11} strokeWidth={1.5} /></button>
                    <button data-testid={`fin-action-${r.client_id}`} onClick={() => setActionFor({ client_id: r.client_id, client_name: r.client_name })} className="btn-ghost !py-1 !px-2 text-xs">Action</button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={20} className="text-center text-[var(--ink-2)] py-12">No clients match.</td></tr>}
          </tbody>
        </table>
      </div>

      {actionFor && <RaiseActionModal target={actionFor} onClose={(saved) => { setActionFor(null); if (saved) onReload(); }} />}
      {detailFor && <ClientDetailPanel row={detailFor} onClose={() => setDetailFor(null)} onRaiseAction={(t) => { setDetailFor(null); setActionFor(t); }} />}
    </>
  );
}

function EditableDate({ canEdit, value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  const display = value ? (value || "").slice(0, 10) : "—";
  if (!canEdit) return <td className="font-mono-data text-xs">{display}</td>;
  if (editing) {
    return (
      <td className="!py-0">
        <input type="date" data-testid={testid} className="input-slim w-32 text-xs" value={(v || "").slice(0, 10)} autoFocus onChange={(e) => setV(e.target.value)}
          onBlur={() => { onSave(v || ""); setEditing(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") { onSave(v || ""); setEditing(false); } if (e.key === "Escape") { setV(value || ""); setEditing(false); } }} />
      </td>
    );
  }
  return <td className="font-mono-data text-xs cursor-pointer hover:bg-[var(--gold-soft)]" onClick={() => setEditing(true)} title="Click to edit">{display}</td>;
}

function EditableDMYDate({ value, onSave, testid }) {
  // DD/MM/YYYY freeform (per user spec REQ-1.3)
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  if (editing) {
    return (
      <td className="!py-0">
        <input type="text" data-testid={testid} placeholder="DD/MM/YYYY" className="input-slim w-28 text-xs" value={v} autoFocus onChange={(e) => setV(e.target.value)}
          onBlur={() => { onSave(v || ""); setEditing(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") { onSave(v || ""); setEditing(false); } if (e.key === "Escape") { setV(value || ""); setEditing(false); } }} />
      </td>
    );
  }
  return <td className="font-mono-data text-xs cursor-pointer hover:bg-[var(--gold-soft)]" onClick={() => setEditing(true)} title="Click to edit (DD/MM/YYYY)">{value || "—"}</td>;
}


function EditableNumber({ canEdit, value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || 0);
  useEffect(() => setV(value || 0), [value]);
  if (!canEdit) return <td className="font-mono-data text-xs">{value || "—"}</td>;
  if (editing) {
    return (
      <td className="!py-0">
        <input type="number" min="0" step="1" data-testid={testid} className="input-slim w-16 text-xs" value={v} autoFocus onChange={(e) => setV(e.target.value)}
          onBlur={() => { onSave(v || 0); setEditing(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") { onSave(v || 0); setEditing(false); } if (e.key === "Escape") { setV(value || 0); setEditing(false); } }} />
      </td>
    );
  }
  return <td className="font-mono-data text-xs cursor-pointer hover:bg-[var(--gold-soft)]" onClick={() => setEditing(true)} title="Click to edit">{value || "—"}</td>;
}

function EditableMoney({ canEdit, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!canEdit) return <td className="font-mono-data text-xs">{fmt(value)}</td>;
  if (editing) {
    return (
      <td className="!py-0">
        <input type="number" step="0.01" className="input-slim w-24 text-xs" value={v} autoFocus onChange={(e) => setV(e.target.value)}
          onBlur={() => { onSave(parseFloat(v || 0)); setEditing(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") { onSave(parseFloat(v || 0)); setEditing(false); } if (e.key === "Escape") { setV(value); setEditing(false); } }} />
      </td>
    );
  }
  return <td className="font-mono-data text-xs cursor-pointer hover:bg-[var(--gold-soft)]" onClick={() => setEditing(true)} title="Click to edit">{fmt(value)}</td>;
}

/* -------------------------------------------------- Monthly Tracker */
function MonthlyTracker({ rows, year, maxNext, canEdit, isAdmin, isManager, onReload }) {
  const showImport = isAdmin || isManager;
  const importRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const yy = String(year).slice(-2);
  const nyy = String(year + 1).slice(-2);
  // Columns list: 12 current-year months + (maxNext) months of year+1
  const cols = [
    ...MONTHS.map((m, i) => ({ label: `${m}-${yy}`, y: year, idx: i })),
    ...Array.from({ length: maxNext || 0 }, (_, k) => ({ label: `${MONTHS[k]}-${nyy}`, y: year + 1, idx: k })),
  ];

  const cellOf = (r, col) => {
    if (col.y === year) return r.months[col.idx] || {};
    return (r.next_year_months || [])[col.idx] || {};
  };

  const totals = cols.map((col) => {
    let f = 0, a = 0;
    for (const r of rows) {
      const m = cellOf(r, col);
      f += (m.forecast_standard || 0) + (m.forecast_additional || 0);
      a += (m.actual_standard || 0) + (m.actual_additional || 0);
    }
    return { f, a };
  });

  // "elapsed" is column-index-based; in the current year, elapsed is months to today; in year+1 all cells editable if today ≥ their month
  const today = new Date();
  const isPast = (col) => {
    const d = new Date(col.y, col.idx + 1, 0); // last day of that month
    return d <= today;
  };

  async function handleTrackerImport(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true); setImportMsg("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${API}/billing/${year}/tracker-import`, { method: "POST", body: fd, credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Import failed");
      const skipped = (j.results || []).filter(x => !x.ok);
      setImportMsg(`Imported actuals for ${j.ok}/${j.total} clients${skipped.length ? " · " + skipped.length + " skipped" : ""}`);
      onReload();
    } catch (err) {
      setImportMsg(`Failed: ${err.message}`);
    }
    setImporting(false);
    if (importRef.current) importRef.current.value = "";
  }

  return (
    <>
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <div className="text-xs text-[var(--ink-2)]">Click a past-month Actual cell to edit inline. Xero sync coming soon — for now use bulk import.</div>
        <div className="flex-1" />
        {showImport && (
          <>
            <button data-testid="mt-import-btn" disabled={importing} onClick={() => importRef.current?.click()} className="btn-ghost flex items-center gap-2 text-xs" title="Bulk import monthly actuals (CSV / XLSX)">
              <Upload size={12} strokeWidth={1.5} /> {importing ? "Importing…" : "Import Actuals"}
            </button>
            <input ref={importRef} type="file" accept=".csv,.xlsx" onChange={handleTrackerImport} className="hidden" data-testid="mt-import-file" />
          </>
        )}
        <a data-testid="mt-export-btn" href={`${API}/billing/${year}/tracker-export`} className="btn-ghost flex items-center gap-2 text-xs" title="Export monthly tracker to CSV">
          <Download size={12} strokeWidth={1.5} /> Export
        </a>
      </div>
      {importMsg && <div className="text-xs mb-3 text-[var(--ink-2)]" data-testid="mt-import-msg">{importMsg}</div>}
    <div className="card-flat overflow-x-auto">
      <table className="tbl text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 bg-[var(--surface)] z-10 min-w-[200px]">Client</th>
            {cols.map((c) => (
              <th key={c.label} colSpan={2} className={`text-center border-l border-[var(--line)] ${isPast(c) ? "" : "text-[var(--ink-2)]"}`}>{c.label}</th>
            ))}
          </tr>
          <tr className="text-[10px] text-[var(--ink-2)]">
            <th className="sticky left-0 bg-[var(--surface)] z-10" />
            {cols.map((c) => (
              <React.Fragment key={c.label}>
                <th className="text-right pr-1 border-l border-[var(--line)]">Fcst</th>
                <th className="text-right pr-1">Act</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.client_id} data-testid={`mt-row-${r.client_id}`}>
              <td className="sticky left-0 bg-[var(--surface)] z-10 font-medium">{r.client_name}</td>
              {cols.map((col) => {
                const m = cellOf(r, col);
                const past = isPast(col);
                const f = (m.forecast_standard || 0) + (m.forecast_additional || 0);
                const a = (m.actual_standard || 0) + (m.actual_additional || 0);
                const underRatio = f > 0 ? a / f : 1;
                const flag = past && f > 0 && underRatio < 0.85;
                return (
                  <React.Fragment key={col.label}>
                    <td className="text-right font-mono-data text-[10px] text-[var(--ink-2)] border-l border-[var(--line)] pr-1">{f > 0 ? fmt(f) : "—"}</td>
                    <MonthCell
                      canEdit={canEdit && past}
                      value={a}
                      display={past ? (a > 0 ? fmt(a) : "—") : "—"}
                      cls={flag ? "text-[var(--err)] bg-[var(--err-bg)]" : ""}
                      onSave={async (v) => {
                        const fs = m.forecast_standard || 0; const fa = m.forecast_additional || 0;
                        const tot = fs + fa;
                        let asv = v, aav = 0;
                        if (tot > 0) { asv = Math.round((v * fs / tot) * 100) / 100; aav = Math.round((v * fa / tot) * 100) / 100; }
                        await api.patch(`/billing/${col.y}/client/${r.client_id}/month/${col.idx + 1}`, { actual_standard: asv, actual_additional: aav });
                        onReload();
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
          <tr className="font-medium bg-[var(--bg)]">
            <td className="sticky left-0 bg-[var(--bg)] z-10">TOTAL</td>
            {totals.map((t, i) => (
              <React.Fragment key={i}>
                <td className="text-right font-mono-data text-[10px] border-l border-[var(--line)] pr-1">{t.f > 0 ? fmt(t.f) : "—"}</td>
                <td className="text-right font-mono-data text-[10px] pr-1">{t.a > 0 ? fmt(t.a) : "—"}</td>
              </React.Fragment>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
    </>
  );
}

function MonthCell({ canEdit, value, display, cls, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!canEdit) return <td className={`text-right font-mono-data text-[10px] pr-1 ${cls}`}>{display}</td>;
  if (editing) {
    return (
      <td className="!py-0 pr-0.5">
        <input type="number" step="0.01" className="input-slim w-20 text-[10px]" value={v} autoFocus onChange={(e) => setV(e.target.value)}
          onBlur={() => { onSave(parseFloat(v || 0)); setEditing(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") { onSave(parseFloat(v || 0)); setEditing(false); } if (e.key === "Escape") { setV(value); setEditing(false); } }}
        />
      </td>
    );
  }
  return <td className={`text-right font-mono-data text-[10px] pr-1 cursor-pointer hover:bg-[var(--gold-soft)] ${cls}`} onClick={() => setEditing(true)} title="Click to edit actual">{display}</td>;
}


/* -------------------------------------------------- Xero Invoices tab */

// Date range presets used in the Invoice Date + Due Date filters.
// Base date presets — Yearly options ("Year 2026", "Year 2025", …) are generated at
// render time from the actual dataset (see XeroInvoices) so the dropdown never shows
// empty years.
const DATE_PRESETS = [
  { key: "all", label: "All dates" },
  { key: "this-month", label: "This month" },
  { key: "last-month", label: "Last month" },
  { key: "this-quarter", label: "This quarter" },
  { key: "this-year", label: "This year" },
  { key: "last-year", label: "Last year" },
  { key: "overdue", label: "Overdue only (past due date)" },  // Due-date filter only
  { key: "custom", label: "Custom…" },
];

function _preset_to_range(preset) {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (preset === "this-month") return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  if (preset === "last-month") return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  if (preset === "this-quarter") { const q = Math.floor(m / 3); return { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) }; }
  if (preset === "this-year") return { from: `${y}-01-01`, to: `${y}-12-31` };
  if (preset === "last-year") return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  // Dynamic year presets: "year-YYYY"
  if (preset && preset.startsWith("year-")) {
    const yr = preset.slice(5);
    return { from: `${yr}-01-01`, to: `${yr}-12-31` };
  }
  return { from: "", to: "" };
}

/* ---------- Xero table column definitions — invoice cols + 9 client-meta cols (REQ-1.8/1.9/1.10) ---------- */
const XERO_COLS = [
  // Invoice-level fields (from the uploaded Xero CSV — not editable)
  { key: "client",       label: "Client",      align: "left",  defaultVisible: true, minWidth: "140px", group: "inv", xero: true },
  { key: "invoice_num",  label: "Invoice #",   align: "left",  defaultVisible: true, minWidth: "100px", group: "inv", xero: true },
  { key: "description",  label: "Description", align: "left",  defaultVisible: true, minWidth: "220px", group: "inv", xero: true },
  { key: "inv_date",     label: "Inv Date",    align: "left",  defaultVisible: true, minWidth: "90px",  group: "inv", xero: true },
  { key: "due_date",     label: "Due Date",    align: "left",  defaultVisible: true, minWidth: "90px",  group: "inv", xero: true },
  { key: "total",        label: "Total",       align: "right", defaultVisible: true, minWidth: "80px",  group: "inv", xero: true },
  { key: "paid",         label: "Paid",        align: "right", defaultVisible: true, minWidth: "80px",  group: "inv", xero: true },
  { key: "due",          label: "Due",         align: "right", defaultVisible: true, minWidth: "80px",  group: "inv", xero: true },
  { key: "status",       label: "Status",      align: "left",  defaultVisible: true, minWidth: "110px", group: "inv", xero: true },
  { key: "invoice_age",  label: "Invoice Age", align: "right", defaultVisible: true, minWidth: "80px", group: "inv" },
  { key: "days_overdue", label: "Days Overdue", align: "right", defaultVisible: true, minWidth: "80px", group: "inv" },
  // Client-level fields moved from Client Billing (REQ-1.8) — editable, held per-client
  { key: "relationship_start_date",     label: "Relationship Start", align: "left",  defaultVisible: false, minWidth: "110px", group: "meta", editable: true },
  { key: "business_type",               label: "Business Type",      align: "left",  defaultVisible: false, minWidth: "110px", group: "meta", editable: true, hasFilter: true },
  { key: "account_manager",             label: "Account Manager",    align: "left",  defaultVisible: true,  minWidth: "130px", group: "meta", editable: true, hasFilter: true },
  { key: "invoice_renewal_month",       label: "Renewal Month",      align: "left",  defaultVisible: false, minWidth: "110px", group: "meta", editable: true },
  { key: "payment_method",              label: "Payment Method",     align: "left",  defaultVisible: false, minWidth: "150px", group: "meta", editable: true, hasFilter: true },
  { key: "billing_frequency",           label: "Billing Frequency",  align: "left",  defaultVisible: true,  minWidth: "110px", group: "meta", editable: true, hasFilter: true },
  { key: "client_status_overall_billing", label: "Invoice Payment Status", align: "left", defaultVisible: true, minWidth: "210px", group: "meta", editable: true, hasFilter: true },
  { key: "partner_payment_fcst",        label: "Partner Payment (FCST)", align: "right", defaultVisible: false, minWidth: "120px", group: "meta", editable: true },
  { key: "partner_payment_ytd",         label: "Partner Payment (YTD)",  align: "right", defaultVisible: false, minWidth: "120px", group: "meta", editable: true },
  // Actions (Chase button etc.)
  { key: "actions",      label: "Tasks",       align: "right", defaultVisible: true, minWidth: "60px", group: "inv" },
  { key: "cc_action",    label: "Credit Control Action", align: "left", defaultVisible: true, minWidth: "150px", group: "inv" },
  { key: "cc_stage",     label: "Credit Control Stage",  align: "left", defaultVisible: true, minWidth: "140px", group: "inv" },
  { key: "mp_stage",     label: "Missed Payment Stage",  align: "left", defaultVisible: true, minWidth: "140px", group: "inv" },
];
const CC_STAGES = ["CC-Week 1", "CC-Week 2", "CC-Escalation 1", "CC-Escalation 2", "CC-Management Review"];
const MP_STAGES = ["MP-Week 1", "MP-Week 2", "MP-Escalation", "MP-Escalation 2", "MP-Management Review"];

// Enum values for the meta dropdowns — mirror the Literal[] in backend models.py::ClientIn
const XERO_META_BUSINESS_TYPES = ["Pvt Ltd", "LLP", "Self Assessment", "Other"];
const XERO_META_PAYMENT_METHODS = ["Direct Debit - Not Requested", "Direct Debit - Requested", "Direct Debit - In Place", "Bank Transfer"];
const XERO_META_BILLING_FREQS = ["Monthly", "Annual", "One-Off"];
const XERO_META_CLIENT_STATUSES = ["On Track", "In Arrears - Invoice Overdue", "In Arrears - Missed Payment", "Paid"];
const DO_BANDS = [["", "All"], ["0", "Not overdue (0)"], ["1+", "Overdue (1+)"], ["1-30", "1–30 days"], ["31-60", "31–60 days"], ["61-90", "61–90 days"], ["90+", "Over 90 days"]];
const AGE_BANDS = [["", "All"], ["0-30", "0–30 days"], ["31-60", "31–60 days"], ["61-90", "61–90 days"], ["90+", "Over 90 days"]];
const _inBand = (n, band) => {
  if (!band) return true;
  if (n === null) return false;
  if (band === "0") return n === 0;
  if (band === "1+") return n >= 1;
  if (band === "90+") return n > 90;
  const [a, b] = band.split("-").map(Number);
  return n >= a && n <= b;
};
const XERO_META_RENEWAL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function XeroInvoices({ year, isAdmin, isManager, onReload }) {
  const showImport = isAdmin || isManager;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [clientFilter, setClientFilter] = useState(""); // "" = All
  const [clientList, setClientList] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [invPreset, setInvPreset] = useState("all");
  const [doBand, setDoBand] = useState("");
  const [ageBand, setAgeBand] = useState("");
  const [ccStageF, setCcStageF] = useState([]);
  const [mpStageF, setMpStageF] = useState([]);
  const [invRange, setInvRange] = useState({ from: "", to: "" });
  const [duePreset, setDuePreset] = useState("all");
  const [dueRange, setDueRange] = useState({ from: "", to: "" });
  const [chasing, setChasing] = useState(null); // invoice row when the Debtor Chase modal is open
  // v1.18: column visibility — persisted to localStorage so the accountant's layout sticks.
  // v1.19: bumped the storage key because we added 9 new columns; old saved state would miss them.
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("xero-visible-cols-v3") || "null");
      if (saved && typeof saved === "object") return saved;
    } catch { /* fall through */ }
    return Object.fromEntries(XERO_COLS.map((c) => [c.key, c.defaultVisible]));
  });
  useEffect(() => { localStorage.setItem("xero-visible-cols-v3", JSON.stringify(visibleCols)); }, [visibleCols]);
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  // v1.19 REQ-1.9/1.10: filter state for the 5 filterable client-meta columns
  const [metaFilters, setMetaFilters] = useState({
    business_type: "", account_manager: "", payment_method: "",
    billing_frequency: "", client_status_overall_billing: "",
  });
  // Cache of client meta edits in-flight so table stays snappy while PATCH is on the wire
  const [metaSaving, setMetaSaving] = useState({});
  const fileRef = useRef(null);

  async function load() {
    const params = new URLSearchParams({ limit: 5000 });
    if (q) params.set("q", q);
    const r = await api.get(`/xero/invoices?${params.toString()}`);
    setRows(r.data.rows);
  }
  async function loadClients() {
    try {
      const r = await api.get("/xero/clients");
      setClientList(r.data);
    } catch { /* ignore */ }
  }
  useEffect(() => { load(); loadClients(); /* eslint-disable-next-line */ }, []);

  async function handleImport(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${API}/xero/import`, { method: "POST", body: fd, credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Import failed");
      setImportResult(j);
      await load();
      await loadClients();
      onReload();
    } catch (err) {
      setImportResult({ error: err.message });
    }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  const _bucket = (s) => {
    const u = (s || "").trim().toUpperCase();
    if (u === "PAID") return "Paid";
    if (["AWAITING PAYMENT", "AUTHORISED", "SUBMITTED"].includes(u)) return "Awaiting Payment";
    if (u === "DRAFT") return "Draft";
    return null;
  };
  const _isOverdue = (r) => _bucket(r.status) === "Awaiting Payment" && r.due_date && r.due_date < new Date().toISOString().slice(0, 10);
  // Calendar days overdue for unpaid / part-paid invoices; null when not applicable.
  const _dayDiff = (iso) => {
    const t = new Date(); const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    return Math.round((Date.UTC(t.getFullYear(), t.getMonth(), t.getDate()) - Date.UTC(y, m - 1, d)) / 86400000);
  };
  // Invoice Payment Status: "Paid" when Xero says paid, blank for drafts, else the client's billing status.
  const _payStatusOverride = (r) => { const b = _bucket(r.status); return b === "Paid" ? "Paid" : b === "Draft" ? "" : null; };
  const _metaVal = (r, k) => (k === "client_status_overall_billing" && _payStatusOverride(r) !== null ? _payStatusOverride(r) : ((r.client_meta || {})[k] || ""));
  const _invoiceAge = (r) => (r.invoice_date ? Math.max(0, _dayDiff(r.invoice_date)) : null);
  const _daysOverdue = (r) => {
    if (_bucket(r.status) !== "Awaiting Payment" || !(r.amount_due > 0) || !r.due_date) return null;
    const t = new Date(); const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
    const [y, m, d] = r.due_date.slice(0, 10).split("-").map(Number);
    return Math.max(0, Math.round((today - Date.UTC(y, m - 1, d)) / 86400000));
  };

  const invEffective = useMemo(() => invPreset === "custom" ? invRange : invPreset === "all" || invPreset === "overdue" ? { from: "", to: "" } : _preset_to_range(invPreset), [invPreset, invRange]);
  const dueEffective = useMemo(() => duePreset === "custom" ? dueRange : duePreset === "all" ? { from: "", to: "" } : _preset_to_range(duePreset), [duePreset, dueRange]);

  // Build the list of "Year YYYY" preset options from the actual invoice_date years in the
  // dataset so we never show an empty year. Descending (newest first).
  const yearPresets = useMemo(() => {
    const yrs = new Set();
    rows.forEach((r) => { if (r.invoice_date) yrs.add(r.invoice_date.slice(0, 4)); });
    return Array.from(yrs).sort((a, b) => b.localeCompare(a));
  }, [rows]);

  // ----- Row filtering -----
  // We compute TWO filtered sets so we can display "N of M shown — Y hidden by other filters"
  // when a client filter is active. `filteredRows` is what the table actually renders;
  // `clientOnlyRows` is what the row would look like with only the client filter applied.
  const _matchesNonClientFilters = (r) => {
    if (status && r.status !== status) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!((r.invoice_number || "").toLowerCase().includes(s)
        || (r.contact_name_raw || "").toLowerCase().includes(s)
        || (r.description || "").toLowerCase().includes(s))) return false;
    }
    if (invEffective.from && (r.invoice_date || "") < invEffective.from) return false;
    if (invEffective.to && (r.invoice_date || "") > invEffective.to) return false;
    if (duePreset === "overdue") { if (!_isOverdue(r)) return false; }
    else {
      if (dueEffective.from && (r.due_date || "") < dueEffective.from) return false;
      if (dueEffective.to && (r.due_date || "") > dueEffective.to) return false;
    }
    if (doBand && !_inBand(_daysOverdue(r), doBand)) return false;
    if (ageBand && !_inBand(_invoiceAge(r), ageBand)) return false;
    if (ccStageF.length && !ccStageF.includes(r.cc_stage || NO_STAGE)) return false;
    if (mpStageF.length && !mpStageF.includes(r.mp_stage || NO_STAGE)) return false;
    // v1.19: client-meta filters (REQ-1.9/1.10) — match against joined client_meta.
    for (const k of Object.keys(metaFilters)) {
      if (metaFilters[k] && _metaVal(r, k) !== metaFilters[k]) return false;
    }
    return true;
  };
  const _matchesClientFilter = (r) => {
    if (!clientFilter) return true;
    const key = r.matched_client_id || `__raw__${r.contact_name_raw || ""}`;
    return key === clientFilter;
  };
  const filteredRows = useMemo(() => rows.filter((r) => _matchesClientFilter(r) && _matchesNonClientFilters(r)), [rows, status, clientFilter, q, invEffective, dueEffective, duePreset, metaFilters, doBand, ageBand, ccStageF, mpStageF]);
  const clientOnlyRows = useMemo(() => clientFilter ? rows.filter(_matchesClientFilter) : [], [rows, clientFilter]);
  const hiddenByOtherFilters = clientOnlyRows.length - filteredRows.length;

  const totals = useMemo(() => filteredRows.reduce((acc, r) => {
    const b = _bucket(r.status);
    if (b === "Paid" || b === "Awaiting Payment") {
      acc.invoiced += (r.total || 0);
      acc.paid += (r.amount_paid || 0);
      acc.outstanding += (r.amount_due || 0);
      if (_isOverdue(r)) { acc.overdue += (r.amount_due || 0); acc.overdue_count += 1; }
    } else if (b === "Draft") {
      acc.draft += (r.total || 0);
      acc.draft_count += 1;
    }
    return acc;
  }, { invoiced: 0, paid: 0, outstanding: 0, overdue: 0, overdue_count: 0, draft: 0, draft_count: 0 }), [filteredRows]);

  const selectedClient = clientFilter ? clientList.find((c) => (c.client_id || `__raw__${c.name}`) === clientFilter) : null;
  const clientPanel = useMemo(() => {
    if (!selectedClient) return null;
    let invoiced = 0, paid = 0, outstanding = 0, overdue = 0, overdue_ct = 0;
    let draft_ct = 0, draft_val = 0, paid_ct = 0, awaiting_ct = 0;
    let latestInvoice = "", firstInvoice = "";
    let sumDaysToPay = 0, daysToPayCount = 0;
    clientOnlyRows.forEach((r) => {
      const b = _bucket(r.status);
      if (b === "Paid" || b === "Awaiting Payment") {
        invoiced += (r.total || 0); paid += (r.amount_paid || 0); outstanding += (r.amount_due || 0);
        if (_isOverdue(r)) { overdue += (r.amount_due || 0); overdue_ct += 1; }
      }
      if (b === "Paid") {
        paid_ct += 1;
        if (r.invoice_date && r.due_date) {
          const dt = (new Date(r.due_date) - new Date(r.invoice_date)) / (1000 * 60 * 60 * 24);
          if (Number.isFinite(dt) && dt >= 0) { sumDaysToPay += dt; daysToPayCount += 1; }
        }
      } else if (b === "Awaiting Payment") awaiting_ct += 1;
      else if (b === "Draft") { draft_ct += 1; draft_val += (r.total || 0); }
      if (r.invoice_date) {
        if (!latestInvoice || r.invoice_date > latestInvoice) latestInvoice = r.invoice_date;
        if (!firstInvoice || r.invoice_date < firstInvoice) firstInvoice = r.invoice_date;
      }
    });
    const avgDaysToPay = daysToPayCount > 0 ? Math.round(sumDaysToPay / daysToPayCount) : null;
    return { invoiced, paid, outstanding, overdue, overdue_ct, draft_ct, draft_val, paid_ct, awaiting_ct, latestInvoice, firstInvoice, avgDaysToPay, totalInvoices: clientOnlyRows.length };
  }, [selectedClient, clientOnlyRows]);

  const statusPill = (s, isOverdue) => {
    const base = "status-pill text-[9px] !py-0 !px-1.5";
    const b = _bucket(s);
    if (b === "Paid") return `${base} border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]`;
    if (b === "Awaiting Payment") return isOverdue
      ? `${base} border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]`
      : `${base} border-[var(--warn)] text-[var(--warn)] bg-[var(--gold-soft)]`;
    if (b === "Draft") return `${base} border-[var(--line)] text-[var(--ink-2)] bg-[var(--bg)]`;
    return `${base} border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]`;
  };

  const statusOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.status) set.add(r.status); });
    return Array.from(set).sort();
  }, [rows]);

  const anyMetaFilter = Object.values(metaFilters).some(Boolean);
  const anyFilter = q || status || clientFilter || invPreset !== "all" || duePreset !== "all" || anyMetaFilter;

  // Visible column list (in declared order)
  const visibleColList = useMemo(() => XERO_COLS.filter((c) => visibleCols[c.key] !== false), [visibleCols]);

  // Cell renderers — one place per column so it's easy to add/reorder later
  function renderCell(colKey, r, overdue) {
    const patchCC = async (upd) => {
      setRows((prev) => prev.map((row) => (row.invoice_number === r.invoice_number ? { ...row, ...upd } : row)));
      try { await api.patch(`/xero/invoice/${encodeURIComponent(r.invoice_number)}`, upd); }
      catch (e) {
        setRows((prev) => prev.map((row) => (row.invoice_number === r.invoice_number ? { ...row, ...Object.fromEntries(Object.keys(upd).map((k) => [k, r[k]])) } : row)));
        alert(`Couldn't save: ${formatErr(e.response?.data?.detail)}`);
      }
    };
    if (colKey === "client") return (
      <>
        <span className="truncate block max-w-[160px]" title={r.contact_name_raw}>{r.contact_name_raw}</span>
        {!r.matched_client_id && <span className="status-pill !text-[8px] !py-0 !px-1 border-[var(--err)] text-[var(--err)] mt-0.5 inline-block">UNMATCHED</span>}
      </>
    );
    if (colKey === "invoice_num") return <span className="font-mono-data">{r.invoice_number}</span>;
    if (colKey === "description") return (
      <span className="line-clamp-2 block leading-tight text-[var(--ink-2)]" title={r.description || ""}>{r.description || "—"}</span>
    );
    if (colKey === "inv_date") return <span className="whitespace-nowrap">{r.invoice_date || "—"}</span>;
    if (colKey === "due_date") return <span className={`whitespace-nowrap ${overdue ? "text-[var(--err)] font-medium" : ""}`}>{r.due_date || "—"}</span>;
    if (colKey === "invoice_age") {
      const n = _invoiceAge(r);
      return <span data-testid={`xero-invoice-age-${r.invoice_number}`} className="font-mono-data text-[var(--ink-2)]">{n === null ? "—" : n}</span>;
    }
    if (colKey === "days_overdue") {
      const n = _daysOverdue(r);
      return <span data-testid={`xero-days-overdue-${r.invoice_number}`} className={`font-mono-data ${n > 60 ? "text-[var(--err)] font-semibold" : n > 0 ? "text-[var(--err)]" : "text-[var(--ink-2)]"}`}>{n === null ? "—" : n}</span>;
    }
    if (colKey === "total") return <span className="font-mono-data">{fmt(r.total)}</span>;
    if (colKey === "paid") return <span className="font-mono-data text-[var(--ok)]">{fmt(r.amount_paid)}</span>;
    if (colKey === "due") return <span className={`font-mono-data ${r.amount_due > 0 ? (overdue ? "text-[var(--err)]" : "text-[var(--warn)]") : "text-[var(--ink-2)]"}`}>{fmt(r.amount_due)}</span>;
    if (colKey === "status") return <span className={statusPill(r.status, overdue)}>{overdue ? "OVERDUE" : (r.status || "—")}</span>;

    // ---- v1.19: 9 client-meta cells (REQ-1.8) — editable inline, blank when unmatched ----
    if (["relationship_start_date","business_type","account_manager","invoice_renewal_month","payment_method","billing_frequency","client_status_overall_billing","partner_payment_fcst","partner_payment_ytd"].includes(colKey)) {
      const cid = r.matched_client_id;
      if (colKey === "client_status_overall_billing" && _payStatusOverride(r) !== null) return <span data-testid={`xero-meta-${colKey}-${r.invoice_number}`} className="text-[10px] text-[var(--ok)] font-medium px-1">{_payStatusOverride(r)}</span>;
      if (!cid) return <span className="text-[var(--ink-2)] italic text-[10px]">—</span>;
      const meta = r.client_meta || {};
      const val = meta[colKey];
      const savingKey = `${cid}::${colKey}`;
      const isSaving = metaSaving[savingKey];
      const commonCls = "input-slim !py-0.5 !px-1 !text-[10px] w-full bg-transparent hover:bg-[var(--bg)] focus:bg-[var(--surface)] disabled:opacity-50";
      const patchMeta = async (newVal) => {
        setMetaSaving((s) => ({ ...s, [savingKey]: true }));
        // Optimistic update — user sees the new value immediately
        const prevVal = val;
        setRows((prev) => prev.map((row) => row.matched_client_id === cid ? { ...row, client_meta: { ...(row.client_meta || {}), [colKey]: newVal } } : row));
        try {
          await api.patch(`/clients/${cid}`, { [colKey]: newVal });
        } catch (e) {
          // Roll back on error and tell the user
          setRows((prev) => prev.map((row) => row.matched_client_id === cid ? { ...row, client_meta: { ...(row.client_meta || {}), [colKey]: prevVal } } : row));
          alert(`Couldn't save ${colKey}: ${formatErr(e)}`);
        }
        setMetaSaving((s) => { const c = { ...s }; delete c[savingKey]; return c; });
      };
      if (colKey === "business_type") return (
        <select disabled={isSaving} className={commonCls} value={val || ""} onChange={(e) => patchMeta(e.target.value)} data-testid={`xero-meta-${colKey}-${r.invoice_number}`}><option value="">—</option>{XERO_META_BUSINESS_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}</select>
      );
      if (colKey === "payment_method") return (
        <select disabled={isSaving} className={commonCls} value={val || ""} onChange={(e) => patchMeta(e.target.value)} data-testid={`xero-meta-${colKey}-${r.invoice_number}`}><option value="">—</option>{XERO_META_PAYMENT_METHODS.map((v) => <option key={v} value={v}>{v}</option>)}</select>
      );
      if (colKey === "billing_frequency") return (
        <select disabled={isSaving} className={commonCls} value={val || ""} onChange={(e) => patchMeta(e.target.value)} data-testid={`xero-meta-${colKey}-${r.invoice_number}`}><option value="">—</option>{XERO_META_BILLING_FREQS.map((v) => <option key={v} value={v}>{v}</option>)}</select>
      );
      if (colKey === "client_status_overall_billing") return (
        <select disabled={isSaving} className={commonCls} value={val || ""} onChange={(e) => patchMeta(e.target.value)} data-testid={`xero-meta-${colKey}-${r.invoice_number}`}><option value="">—</option>{[...XERO_META_CLIENT_STATUSES, ...(val && !XERO_META_CLIENT_STATUSES.includes(val) ? [val] : [])].map((v) => <option key={v} value={v}>{v}</option>)}</select>
      );
      if (colKey === "invoice_renewal_month") return (
        <select disabled={isSaving} className={commonCls} value={val || ""} onChange={(e) => patchMeta(e.target.value)} data-testid={`xero-meta-${colKey}-${r.invoice_number}`}><option value="">—</option>{XERO_META_RENEWAL_MONTHS.map((v) => <option key={v} value={v}>{v}</option>)}</select>
      );
      if (colKey === "partner_payment_fcst" || colKey === "partner_payment_ytd") return (
        <input disabled={isSaving} type="number" step="0.01" defaultValue={val ?? ""} onBlur={(e) => { const nv = e.target.value === "" ? null : Number(e.target.value); if (nv !== val) patchMeta(nv); }} className={`${commonCls} text-right font-mono-data`} data-testid={`xero-meta-${colKey}-${r.invoice_number}`} placeholder="—" />
      );
      // relationship_start_date, account_manager → plain text input
      return (
        <input disabled={isSaving} defaultValue={val || ""} onBlur={(e) => { if (e.target.value !== (val || "")) patchMeta(e.target.value); }} className={commonCls} data-testid={`xero-meta-${colKey}-${r.invoice_number}`} placeholder="—" />
      );
    }

    if (colKey === "cc_action") {
      const Toggle = ({ field, label }) => (
        <button data-testid={`xero-${field}-${r.invoice_number}`} disabled={!showImport} onClick={() => patchCC({ [field]: !r[field] })}
          title={`${label}: click to switch ${r[field] ? "OFF" : "ON"}`}
          className={`inline-flex items-center gap-1 text-[9px] px-1.5 h-[18px] rounded-sm border font-semibold transition-colors disabled:opacity-60 ${r[field] ? "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" : "border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--ink)]"}`}>
          {label} {r[field] ? "ON" : "OFF"}
        </button>
      );
      return <div className="flex gap-1 whitespace-nowrap"><Toggle field="cc_active" label="CC" /><Toggle field="mp_active" label="MP" /></div>;
    }
    if (colKey === "cc_stage" || colKey === "mp_stage") {
      const active = colKey === "cc_stage" ? r.cc_active : r.mp_active;
      if (!active) return <span className="text-[var(--ink-2)] text-[10px]" data-testid={`xero-${colKey}-${r.invoice_number}`}>—</span>;
      return (
        <select data-testid={`xero-${colKey}-${r.invoice_number}`} disabled={!showImport} value={r[colKey] || ""} onChange={(e) => patchCC({ [colKey]: e.target.value })}
          className="input-slim !py-0.5 !px-1 !text-[10px] w-full bg-transparent hover:bg-[var(--bg)]">
          <option value="">—</option>
          {(colKey === "cc_stage" ? CC_STAGES : MP_STAGES).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      );
    }
    if (colKey === "actions") {
      // Task button appears on every Awaiting Payment row (including those that are overdue).
      const isAwaiting = _bucket(r.status) === "Awaiting Payment";
      if (!isAwaiting) return null;
      const cls = overdue
        ? "border-[var(--err)] text-[var(--err)] hover:bg-[var(--err-bg)]"
        : "border-[var(--warn)] text-[var(--warn)] hover:bg-[var(--warn-bg)]";
      return (
        <button
          data-testid={`xero-chase-btn-${r.invoice_number}`}
          onClick={() => setChasing(r)}
          className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border transition ${cls}`}
          title="Raise a task in the Tasks module for this invoice"
        ><Mail size={9} /> Task</button>
      );
    }
    return null;
  }

  return (
    <>
      {/* --------- Compact toolbar: global search + columns menu + import --------- */}
      <div className="flex flex-wrap gap-1.5 items-center mb-2">
        <div className="relative">
          <Search size={11} strokeWidth={1.5} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <input data-testid="xero-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, invoice # or description…" className="input-slim !py-1 !text-[11px] pl-7 w-64" />
        </div>
        {anyFilter && (
          <button data-testid="xero-clear-filters" onClick={() => { setQ(""); setStatus(""); setClientFilter(""); setInvPreset("all"); setDuePreset("all"); setInvRange({ from: "", to: "" }); setDueRange({ from: "", to: "" }); setMetaFilters({ business_type: "", account_manager: "", payment_method: "", billing_frequency: "", client_status_overall_billing: "" }); }} className="text-[10px] text-[var(--ink-2)] hover:text-[var(--navy)] underline">Clear all</button>
        )}
        <div className="flex-1" />
        {/* Column selection dropdown */}
        <div className="relative">
          <button
            data-testid="xero-cols-btn"
            onClick={() => setColsMenuOpen((o) => !o)}
            className="btn-ghost flex items-center gap-1 text-[11px] !py-1 !px-2.5"
            title="Show / hide columns"
          ><TableIcon size={11} /> Columns</button>
          {colsMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setColsMenuOpen(false)} />
              <div className="absolute right-0 mt-1 z-20 bg-[var(--surface)] border border-[var(--line)] rounded shadow-lg p-2 min-w-[180px]" data-testid="xero-cols-menu">
                <div className="text-[9px] uppercase tracking-wider text-[var(--ink-2)] mb-1.5 px-1">Show columns</div>
                {XERO_COLS.filter((c) => c.key !== "actions").map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-[11px] px-1 py-0.5 cursor-pointer hover:bg-[var(--bg)] rounded" data-testid={`xero-col-toggle-${c.key}`}>
                    <input
                      type="checkbox"
                      checked={visibleCols[c.key] !== false}
                      onChange={(e) => setVisibleCols({ ...visibleCols, [c.key]: e.target.checked })}
                      className="!m-0"
                    />
                    {c.label}
                  </label>
                ))}
                <div className="border-t border-[var(--line)] mt-1.5 pt-1.5 flex gap-2 px-1">
                  <button onClick={() => setVisibleCols(Object.fromEntries(XERO_COLS.map((c) => [c.key, true])))} className="text-[10px] text-[var(--navy)] hover:underline">Show all</button>
                  <button onClick={() => setVisibleCols(Object.fromEntries(XERO_COLS.map((c) => [c.key, c.defaultVisible])))} className="text-[10px] text-[var(--ink-2)] hover:underline">Reset</button>
                </div>
              </div>
            </>
          )}
        </div>
        {isAdmin && (
          <>
            <button data-testid="xero-import-btn" disabled={importing} onClick={() => { if (window.confirm("Backup only: uploading a Xero CSV replaces the invoice table. Normally use Sync now instead. Continue?")) fileRef.current?.click(); }} className="btn-ghost flex items-center gap-1.5 text-[10px] !py-1 !px-2 text-[var(--ink-2)]" title="Backup: upload Xero Sales Invoices CSV — replaces the current table">
              <Upload size={10} strokeWidth={1.5} /> {importing ? "Importing…" : "CSV backup"}
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={handleImport} className="hidden" data-testid="xero-import-file" />
          </>
        )}
      </div>

      {/* Summary line */}
      <div className="text-[11px] font-mono-data text-[var(--ink-2)] mb-2" data-testid="xero-summary-line">
        <span data-testid="xero-summary-count">{filteredRows.length} invoice{filteredRows.length === 1 ? "" : "s"}</span>
        {clientFilter && hiddenByOtherFilters > 0 && (
          <span className="ml-2 text-[var(--warn)]" data-testid="xero-client-hidden-hint">
            · {hiddenByOtherFilters} more for this client hidden by other filters
          </span>
        )}
        <span className="mx-1.5">·</span>
        <span>Invoiced <span className="text-[var(--ink)]" data-testid="xero-summary-invoiced">{fmt(totals.invoiced)}</span></span>
        <span className="mx-1.5">·</span>
        <span>Paid <span className="text-[var(--ok)]" data-testid="xero-summary-paid">{fmt(totals.paid)}</span></span>
        <span className="mx-1.5">·</span>
        <span>Outstanding <span className={totals.outstanding > 0 ? "text-[var(--warn)]" : "text-[var(--ink-2)]"} data-testid="xero-summary-outstanding">{fmt(totals.outstanding)}</span></span>
        <span className="mx-1.5">·</span>
        <span>Overdue <span className={totals.overdue > 0 ? "text-[var(--err)]" : "text-[var(--ink-2)]"} data-testid="xero-summary-overdue">{fmt(totals.overdue)}</span> <span className="text-[var(--ink-2)]">({totals.overdue_count})</span></span>
        <span className="mx-1.5">·</span>
        <span data-testid="xero-summary-draft">Draft <span className="text-[var(--ink-2)]">{fmt(totals.draft)}</span> <span className="text-[var(--ink-2)]">({totals.draft_count})</span></span>
      </div>

      {importResult && (
        <div className="card-flat p-2 mb-2 text-[11px]" data-testid="xero-import-result">
          {importResult.error ? (
            <div className="text-[var(--err)]">Import failed: {importResult.error}</div>
          ) : (
            <div className="flex flex-wrap gap-3 items-center">
              <span><b>{importResult.total_invoices}</b> invoices in file</span>
              <span className="text-[var(--ok)]">Loaded: {importResult.created}</span>
              <span>Matched via saved map: {importResult.matched_via_map}</span>
              <span>Matched to existing client: {importResult.matched_via_app}</span>
              <span className="text-[var(--warn)]">Auto-created: {importResult.auto_created_clients}</span>
              {importResult.unmatched > 0 && <span className="text-[var(--err)]">Unmatched: {importResult.unmatched}</span>}
              <span className="text-[var(--ink-2)] italic">Previous table data was replaced.</span>
              <button onClick={() => setImportResult(null)} className="ml-auto text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={11} /></button>
            </div>
          )}
        </div>
      )}

      {selectedClient && clientPanel && (
        <div className="card-flat p-3 mb-2 border-l-2 border-[var(--navy)]" data-testid="xero-client-panel">
          <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-[var(--ink-2)]">Client balance — {clientPanel.totalInvoices} invoice{clientPanel.totalInvoices === 1 ? "" : "s"} on file</div>
              <div className="font-display text-lg" data-testid="xero-client-panel-name">{selectedClient.name}</div>
            </div>
            <div className="text-[10px] text-[var(--ink-2)]">
              {clientPanel.firstInvoice && (<>First invoice: <b className="text-[var(--ink)]">{clientPanel.firstInvoice}</b> · </>)}
              {clientPanel.latestInvoice && (<>Most recent: <b className="text-[var(--ink)]">{clientPanel.latestInvoice}</b></>)}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-[11px]">
            <MiniStat label="Invoiced" value={fmt(clientPanel.invoiced)} />
            <MiniStat label="Paid" value={fmt(clientPanel.paid)} tone="ok" />
            <MiniStat label="Outstanding" value={fmt(clientPanel.outstanding)} tone={clientPanel.outstanding > 0 ? "warn" : "muted"} />
            <MiniStat label="Overdue" value={fmt(clientPanel.overdue)} sub={`${clientPanel.overdue_ct} invoice${clientPanel.overdue_ct === 1 ? "" : "s"}`} tone={clientPanel.overdue > 0 ? "err" : "muted"} />
            <MiniStat label="Draft" value={fmt(clientPanel.draft_val)} sub={`${clientPanel.draft_ct} in pipeline`} tone="muted" />
            <MiniStat label="Avg terms" value={clientPanel.avgDaysToPay != null ? `${clientPanel.avgDaysToPay} days` : "—"} sub={`${clientPanel.paid_ct} paid · ${clientPanel.awaiting_ct} awaiting`} tone="muted" />
          </div>
        </div>
      )}

      {/* Ultra-slim spreadsheet-style table (v1.20) with vertical grid + sticky Client column */}
      <div className="card-flat overflow-x-auto">
        <table className="tbl tbl-ultra" data-testid="xero-table">
          <thead>
            {/* Row 1: column labels */}
            <tr>
              {visibleColList.map((c) => (
                <th
                  key={c.key}
                  data-testid={`xero-col-header-${c.key}`}
                  className={`!py-1.5 ${c.align === "right" ? "text-right" : ""} ${c.key === "client" ? "sticky-col" : ""}`}
                  style={{ minWidth: c.minWidth, ...(c.xero ? { backgroundColor: "#13B5EA", color: "#fff" } : {}) }}
                  title={c.xero ? "Data from Xero" : undefined}
                >{c.label}</th>
              ))}
            </tr>
            {/* Row 2: per-column filters */}
            <tr className="bg-[var(--bg)]/40">
              {visibleColList.map((c) => (
                <th
                  key={`f-${c.key}`}
                  className={`!py-1 !px-1 font-normal ${c.key === "client" ? "sticky-col" : ""}`}
                >
                  {c.key === "client" && (
                    <select data-testid="xero-filter-client" className="input-slim !py-0.5 !px-1.5 !text-[10px] w-full" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                      <option value="">All clients</option>
                      {clientList.map((cl) => {
                        const key = cl.client_id || `__raw__${cl.name}`;
                        return <option key={key} value={key}>{cl.name}</option>;
                      })}
                    </select>
                  )}
                  {c.key === "invoice_num" && (
                    <input
                      data-testid="xero-filter-invnum"
                      placeholder="Search #"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      className="input-slim !py-0.5 !px-1.5 !text-[10px] w-full"
                    />
                  )}
                  {c.key === "description" && (
                    <input
                      data-testid="xero-filter-desc"
                      placeholder="Search description…"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      className="input-slim !py-0.5 !px-1.5 !text-[10px] w-full"
                    />
                  )}
                  {c.key === "inv_date" && (
                    <DatePresetFilter
                      testid="xero-filter-inv-date"
                      preset={invPreset}
                      range={invRange}
                      onPresetChange={setInvPreset}
                      onRangeChange={setInvRange}
                      yearPresets={yearPresets}
                      excludeOverdue
                    />
                  )}
                  {c.key === "due_date" && (
                    <DatePresetFilter
                      testid="xero-filter-due-date"
                      preset={duePreset}
                      range={dueRange}
                      onPresetChange={setDuePreset}
                      onRangeChange={setDueRange}
                      yearPresets={yearPresets}
                    />
                  )}
                  {c.key === "invoice_age" && (
                    <select data-testid="xero-filter-invoice-age" className="input-slim !py-0.5 !px-1.5 !text-[10px] w-full" value={ageBand} onChange={(e) => setAgeBand(e.target.value)}>
                      {AGE_BANDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  )}
                  {c.key === "days_overdue" && (
                    <select data-testid="xero-filter-days-overdue" className="input-slim !py-0.5 !px-1.5 !text-[10px] w-full" value={doBand} onChange={(e) => setDoBand(e.target.value)}>
                      {DO_BANDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  )}
                  {c.key === "cc_stage" && <StageFilter testid="xero-filter-cc-stage" options={CC_STAGES} value={ccStageF} onChange={setCcStageF} />}
                  {c.key === "mp_stage" && <StageFilter testid="xero-filter-mp-stage" options={MP_STAGES} value={mpStageF} onChange={setMpStageF} />}
                  {c.key === "status" && (
                    <select data-testid="xero-filter-status" className="input-slim !py-0.5 !px-1.5 !text-[10px] w-full" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="">All</option>
                      {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                  {/* v1.19 REQ-1.9/1.10: filter dropdowns for the 5 filterable client-meta cols.
                      Options are the distinct values actually present in the joined dataset so
                      empty options don't clutter the dropdown. */}
                  {c.hasFilter && (
                    (() => {
                      const opts = Array.from(new Set(rows.map((r) => _metaVal(r, c.key)).filter(Boolean))).sort();
                      return (
                        <select
                          data-testid={`xero-filter-meta-${c.key}`}
                          className="input-slim !py-0.5 !px-1.5 !text-[10px] w-full"
                          value={metaFilters[c.key] || ""}
                          onChange={(e) => setMetaFilters({ ...metaFilters, [c.key]: e.target.value })}
                        >
                          <option value="">All</option>
                          {opts.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      );
                    })()
                  )}
                  {/* total / paid / due / actions have no filter — empty cell */}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((r) => {
              const overdue = _isOverdue(r);
              return (
                <tr key={r.id} data-testid={`xero-row-${r.invoice_number}`}>
                  {visibleColList.map((c) => (
                    <td
                      key={c.key}
                      className={`!py-1 ${c.align === "right" ? "text-right" : ""} ${c.key === "description" ? "max-w-[280px]" : ""} ${c.key === "client" ? "sticky-col" : ""}`}
                    >
                      {renderCell(c.key, r, overdue)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {filteredRows.length === 0 && <tr><td colSpan={visibleColList.length} className="text-center text-[var(--ink-2)] py-8 text-xs">
              {rows.length === 0
                ? (showImport ? "No invoices yet — click Sync now (top right) to bring them in from Xero." : "No invoices yet.")
                : "No invoices match the current filters."}
            </td></tr>}
          </tbody>
        </table>
      </div>

      {chasing && <DebtorChaseModal invoice={chasing} onClose={() => setChasing(null)} />}
    </>
  );
}

/* --- Compact date-preset filter cell (used inside table header) ---------------------------- */
function DatePresetFilter({ testid, preset, range, onPresetChange, onRangeChange, yearPresets, excludeOverdue }) {
  const [customOpen, setCustomOpen] = useState(false);
  const isCustom = preset === "custom";
  return (
    <div className="relative flex flex-col gap-0.5">
      <select
        data-testid={`${testid}-preset`}
        value={preset}
        onChange={(e) => { onPresetChange(e.target.value); if (e.target.value === "custom") setCustomOpen(true); }}
        className="input-slim !py-0.5 !px-1.5 !text-[10px] w-full"
      >
        {DATE_PRESETS.filter((p) => !(excludeOverdue && p.key === "overdue")).map((p) => (
          <option key={p.key} value={p.key}>{p.label}</option>
        ))}
        {yearPresets && yearPresets.length > 0 && (
          <optgroup label="Year">
            {yearPresets.map((yr) => <option key={yr} value={`year-${yr}`}>{`Year ${yr}`}</option>)}
          </optgroup>
        )}
      </select>
      {isCustom && (
        <div className="flex gap-0.5">
          <input data-testid={`${testid}-from`} type="date" value={range.from} onChange={(e) => onRangeChange({ ...range, from: e.target.value })} className="input-slim !py-0.5 !px-1 !text-[10px] w-full" />
          <input data-testid={`${testid}-to`} type="date" value={range.to} onChange={(e) => onRangeChange({ ...range, to: e.target.value })} className="input-slim !py-0.5 !px-1 !text-[10px] w-full" />
        </div>
      )}
    </div>
  );
}

/* --- Debtor Chase modal — creates a task and/or opens the user's email client (v1.18) -------- */
function DebtorChaseModal({ invoice, onClose }) {
  const [users, setUsers] = useState([]);
  const [client, setClient] = useState(null);
  const [name, setName] = useState(`Chase overdue ${invoice.invoice_number} — ${invoice.contact_name_raw}`);
  const [assigneeId, setAssigneeId] = useState("");
  // ESC key closes the modal (v1.19 UX polish)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [deadline, setDeadline] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10);
  });
  const daysOverdue = invoice.due_date ? Math.max(0, Math.floor((new Date() - new Date(invoice.due_date)) / (1000 * 60 * 60 * 24))) : 0;
  const [description, setDescription] = useState(
    `Invoice ${invoice.invoice_number} — ${fmt(invoice.amount_due || invoice.total)}\n` +
    `Client: ${invoice.contact_name_raw}\n` +
    `Invoice date: ${invoice.invoice_date || "—"}\n` +
    `Due date: ${invoice.due_date || "—"}${daysOverdue > 0 ? ` (${daysOverdue} days overdue)` : ""}\n\n` +
    (invoice.description ? `Description: ${invoice.description}\n\n` : "") +
    `Action: contact the client, confirm payment plan, escalate if no response by ${deadline}.`
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users");
        setUsers(r.data.filter((u) => !u.archived));
      } catch { /* ignore */ }
      // Try to fetch full client record for email
      if (invoice.matched_client_id) {
        try {
          const r = await api.get(`/clients/${invoice.matched_client_id}`);
          setClient(r.data);
        } catch { /* ignore — client may have been archived */ }
      }
    })();
  }, [invoice.matched_client_id]);

  async function createTask() {
    setSaving(true); setErr("");
    try {
      const body = {
        name,
        description,
        client_id: invoice.matched_client_id || null,
        service_type: "Other Business Support",
        task_billing: "part_of_existing",
        assignee_id: assigneeId || null,
        deadline,
        status: "not_started",
        priority: daysOverdue >= 30 ? "high" : "medium",
      };
      const r = await api.post("/tasks", body);
      // /tasks endpoint returns {created: N, tasks: [...]} — take the first newly-created task.
      const created = (r.data && r.data.tasks && r.data.tasks[0]) || r.data;
      setSaved({ task_id: created.id, task_name: created.name || name });
    } catch (e) {
      setErr(formatErr(e));
    }
    setSaving(false);
  }

  function openEmail() {
    const to = client?.email || "";
    const subject = `Overdue invoice ${invoice.invoice_number} — payment reminder`;
    const body = `Dear ${invoice.contact_name_raw},\n\n` +
      `This is a friendly reminder that invoice ${invoice.invoice_number} for ${fmt(invoice.amount_due || invoice.total)} was due on ${invoice.due_date || "—"}${daysOverdue > 0 ? ` and is now ${daysOverdue} day${daysOverdue === 1 ? "" : "s"} overdue` : ""}.\n\n` +
      (invoice.description ? `Details: ${invoice.description}\n\n` : "") +
      `Please could you settle this at your earliest convenience or let us know if there is any issue we should be aware of.\n\n` +
      `Kind regards,\nFinchley Business Services`;
    const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="chase-modal">
      <div className="bg-[var(--surface)] rounded shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--line)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 border-b border-[var(--line)]">
          <div>
            <div className="overline">Debtor chase · {invoice.invoice_number}</div>
            <h2 className="font-display text-xl mt-1">{invoice.contact_name_raw}</h2>
            <div className="text-xs text-[var(--ink-2)] mt-0.5 font-mono-data">
              Due <b className={daysOverdue > 0 ? "text-[var(--err)]" : ""}>{fmt(invoice.amount_due || invoice.total)}</b>
              {daysOverdue > 0 && <> · <span className="text-[var(--err)]">{daysOverdue} day{daysOverdue === 1 ? "" : "s"} overdue</span></>}
              {client?.email && <> · <a href={`mailto:${client.email}`} className="text-[var(--navy)] underline">{client.email}</a></>}
              {!client?.email && invoice.matched_client_id && <> · <span className="italic">No email on client profile</span></>}
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost !p-1" data-testid="chase-close"><X size={14} /></button>
        </div>

        {saved ? (
          <div className="p-4">
            <div className="card-flat p-4 mb-3 border-l-2 border-[var(--ok)]" data-testid="chase-saved">
              <div className="flex items-center gap-2 text-[var(--ok)] font-medium mb-1"><CheckCircle2 size={14} /> Chase task created</div>
              <div className="text-xs text-[var(--ink-2)]">{saved.task_name}</div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={openEmail} className="btn-ghost text-sm" data-testid="chase-email-after-save"><Mail size={12} /> Open email now</button>
              <button onClick={onClose} className="btn-primary text-sm">Done</button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {err && <div className="text-xs text-[var(--err)]" data-testid="chase-err">{err}</div>}
            <div>
              <label className="overline block mb-1">Task title</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="chase-name" className="input-slim w-full text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">Assign to</label>
                <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} data-testid="chase-assignee" className="input-slim w-full text-sm">
                  <option value="">{users.length === 0 ? "Loading users…" : "Unassigned"}</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-1">Deadline</label>
                <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} data-testid="chase-deadline" className="input-slim w-full text-sm" />
              </div>
            </div>
            <div>
              <label className="overline block mb-1">Notes</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="chase-desc" rows={6} className="input-slim w-full text-xs font-mono-data" />
            </div>
            <div className="flex items-center gap-2 justify-end pt-2 border-t border-[var(--line)]">
              <button
                onClick={openEmail}
                disabled={!client?.email && !invoice.matched_client_id}
                data-testid="chase-email"
                className="btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
                title={client?.email ? `Email ${client.email}` : "No email on client profile"}
              ><Mail size={12} /> Open email</button>
              <button
                onClick={createTask}
                disabled={saving || !name.trim()}
                data-testid="chase-save"
                className="btn-primary text-sm inline-flex items-center gap-1.5"
              >{saving ? "Creating…" : "Create chase task"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, tone }) {
  const cls = tone === "ok" ? "text-[var(--ok)]" : tone === "warn" ? "text-[var(--warn)]" : tone === "err" ? "text-[var(--err)]" : tone === "muted" ? "text-[var(--ink-2)]" : "text-[var(--ink)]";
  return (
    <div className="border border-[var(--line)] rounded px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-[var(--ink-2)]">{label}</div>
      <div className={`font-mono-data text-sm ${cls} truncate`}>{value}</div>
      {sub && <div className="text-[9px] text-[var(--ink-2)] truncate">{sub}</div>}
    </div>
  );
}

/* -------------------------------------------------- Raise Action modal */
function RaiseActionModal({ target, onClose }) {
  const [type, setType] = useState(ACTION_TYPES[0]);
  const [note, setNote] = useState("");
  const [due, setDue] = useState(3);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  async function submit() {
    setSaving(true); setErr("");
    try {
      await api.post("/billing/action", { client_id: target.client_id, action_type: type, note, due_in_days: due });
      onClose(true);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    setSaving(false);
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="raise-action-modal">
      <div className="bg-[var(--surface)] rounded shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Raise action</div>
            <h2 className="font-display text-2xl">{target.client_name}</h2>
          </div>
          <button onClick={() => onClose(false)} className="btn-ghost !p-1"><X size={16} /></button>
        </div>
        {err && <div className="text-xs text-[var(--err)] mb-3">{err}</div>}
        <label className="label-overline">Action type</label>
        <select data-testid="action-type" value={type} onChange={(e) => setType(e.target.value)} className="input mb-3">
          {ACTION_TYPES.map(a => <option key={a}>{a}</option>)}
        </select>
        <label className="label-overline">Due in (days)</label>
        <input data-testid="action-due" type="number" min={1} className="input mb-3" value={due} onChange={(e) => setDue(parseInt(e.target.value) || 3)} />
        <label className="label-overline">Note</label>
        <textarea data-testid="action-note" rows={3} className="input mb-4" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional — will appear in the task description." />
        <button data-testid="action-submit" onClick={submit} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
          <CheckCircle2 size={14} strokeWidth={1.5} /> {saving ? "Creating…" : "Create Task"}
        </button>
        <div className="text-[11px] text-[var(--ink-2)] mt-3 leading-relaxed">Creates a task in the main Task module, linked to this client, assigned to their Account Manager, priority High.</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------- Client Detail panel */
function ClientDetailPanel({ row, onClose, onRaiseAction }) {
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    api.get(`/tasks?client_id=${row.client_id}&include_archived=true`).then(r => setTasks(r.data));
  }, [row.client_id]);
  const maxMonthly = Math.max(1, ...row.months.map(m => Math.max((m.forecast_standard || 0) + (m.forecast_additional || 0), (m.actual_standard || 0) + (m.actual_additional || 0))));
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" data-testid="fin-detail-panel" onClick={onClose}>
      <div className="bg-[var(--surface)] w-full max-w-2xl h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline">Client billing detail</div>
            <h2 className="font-display text-3xl">{row.client_name}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost !p-1"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <Kpi label="Annual Forecast" value={fmt(row.annual_forecast)} />
          <Kpi label="YTD Actual" value={fmt(row.ytd_actual_total)} tone="ok" />
          <Kpi label="Remaining" value={fmt(row.remaining_balance)} tone="navy" />
        </div>
        <div className="card-flat p-4 mb-4">
          <div className="overline mb-2">Monthly · Forecast vs Actual</div>
          <div className="grid grid-cols-12 gap-1 items-end h-24">
            {row.months.map((m, i) => {
              const f = (m.forecast_standard || 0) + (m.forecast_additional || 0);
              const a = (m.actual_standard || 0) + (m.actual_additional || 0);
              return (
                <div key={i} className="flex flex-col items-center gap-0.5">
                  <div className="w-full flex items-end justify-center gap-0.5 h-20">
                    <div className="w-1.5 bg-[var(--line)]" style={{ height: `${(f / maxMonthly) * 100}%` }} title={`Fcst ${fmt(f)}`} />
                    <div className="w-1.5 bg-[var(--navy)]" style={{ height: `${(a / maxMonthly) * 100}%` }} title={`Act ${fmt(a)}`} />
                  </div>
                  <div className="text-[8px] text-[var(--ink-2)] font-mono-data">{MONTHS[i][0]}</div>
                </div>
              );
            })}
          </div>
        </div>
        {row.partner_name && (
          <div className="card-flat p-4 mb-4">
            <div className="overline mb-2">Partner · {row.partner_name}</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><div className="text-[var(--ink-2)] text-xs">Forecast (Annual)</div><div className="font-mono-data">{fmt(row.partner_forecast)}</div></div>
              <div><div className="text-[var(--ink-2)] text-xs">Paid (YTD)</div><div className="font-mono-data">{fmt(row.partner_actual)}</div></div>
              <div><div className="text-[var(--ink-2)] text-xs">Status</div><div>{row.partner_status ? <span className={`status-pill text-[10px] ${STATUS_META[row.partner_status]?.cls}`}>{STATUS_META[row.partner_status]?.label}</span> : <span className="text-[var(--ink-2)]">—</span>}</div></div>
            </div>
          </div>
        )}
        <div className="card-flat p-4 mb-4">
          <div className="overline mb-2">Client tasks · {tasks.length}</div>
          {tasks.length === 0 && <div className="text-xs text-[var(--ink-2)]">No tasks yet.</div>}
          <ul className="space-y-1.5">
            {tasks.slice(0, 8).map(t => (
              <li key={t.id} className="flex items-center justify-between text-xs">
                <span className={t.status === "completed" ? "line-through text-[var(--ink-2)]" : ""}>{t.name}</span>
                <span className="font-mono-data text-[var(--ink-2)]">{(t.deadline || "").slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
        <button data-testid="fin-detail-action" onClick={() => onRaiseAction({ client_id: row.client_id, client_name: row.client_name })} className="btn-primary w-full">Raise Action</button>
      </div>
    </div>
  );
}
